import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, get, set, update, onValue, off, push } from 'firebase/database';
import { database } from '../../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../../styles/tutorial.css';
import './Applicants.css';
import NavigationHeader from './NavigationHeader';
import Sidebar from '../Sidebar';
import ApplicantsWorkspace from './ApplicantsWorkspace';
import ProductPreview from './ProductPreview';
import AddSubstagePopup from '../applicants/AddSubstagePopup';
import FilteringSubstagePopup from './FilteringSubstagePopup';
import ApplicantDetailsPopup from './ApplicantDetailsPopup';
import SubscriptionPopup from './SubscriptionPopup';
import GlobalApplicantSearch from './GlobalApplicantSearch';
import NotificationsPopup from './NotificationsPopup';
import useApprovalSettings from '../../hooks/useApprovalSettings';
import InterviewScheduler from '../InterviewScheduler';
import CalendarConnectionPrompt from '../CalendarConnectionPrompt';
import CVUploadManager from './CVUploadManager';
import { CALENDAR_ENABLED } from '../../utils/featureFlags';
import { encryptData } from '../../utils/crypto';
import { NotificationsCache } from './NotificationsCache';
import FeatureGate from '../Trial/FeatureGate';
import { useAccessControl } from '../../hooks/useAccessControl';
import UpgradeModal from '../UpgradeModal';
import {
  debounce,
  processNotificationsEfficiently,
  useUserActivity
} from './NotificationsUtils';
import {
  loadEmployerApplicantsOptimized,
  loadJobApplicantsOptimized,
  updateApplicantPositionOptimized,
  migrateToOptimizedStructure,
  parseConnectionKey,
  loadAllJobOptions
} from './DatabaseOptimizer';
import { emitAtsAnalyticsEvent } from '../../services/atsAnalyticsService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import useTeamAccessProfile from '../../hooks/useTeamAccessProfile';

const ATS_TUTORIAL_JOB_ID = '_ATS_TUTORIAL_JOB_001';
const ATS_TUTORIAL_JOB_TITLE = 'Senior Software Engineer';

const DEMO_INTERVIEW_SUBSTAGE = {
  id: 'interview-tutorial-9002',
  title: 'Initial Interview',
  icon: 'fa-solid fa-calendar-day',
  type: 'interview'
};

const DEMO_ASSESSMENT_SUBSTAGE = {
  id: 'assessment-tutorial-9001',
  title: 'Skill Assessment',
  icon: 'fa-solid fa-clipboard-check',
  type: 'assessment'
};

const ASSESSMENT_SUBSTAGE_TYPES = new Set(['assessment', 'ai-assessment', 'ai-interview']);
const AI_EDITABLE_SUBSTAGE_TYPES = new Set(['ai-assessment', 'ai-interview']);

const baseTutorialWorkflow = () => ({
  'stage-1': {
    name: 'Hiring Workflow',
    substages: {
      'all-applicants-1': {
        id: 'all-applicants-1',
        name: 'All Applicants',
        type: 'static'
      },
      'filter-applicants': {
        id: 'filter-applicants',
        name: 'Fit Applicants',
        type: 'filtering'
      },
      'offer-sent': {
        id: 'offer-sent',
        name: 'Offer Sent',
        type: 'offer'
      },
      'hired-applicants': {
        id: 'hired-applicants',
        name: 'Hired',
        type: 'hired'
      },
      'move-to-onboarding': {
        id: 'move-to-onboarding',
        name: 'Move to Onboarding',
        type: 'onboarding'
      }
    }
  }
});

const cloneData = (value) => JSON.parse(JSON.stringify(value));

const createAtsTutorialDataset = (isRTL) => {
  const tr = (en, ar) => (isRTL ? ar : en);
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  const jobTitle = tr(ATS_TUTORIAL_JOB_TITLE, 'مهندس برمجيات أول (مثال)');

  const job = {
    id: ATS_TUTORIAL_JOB_ID,
    title: jobTitle,
    type: 'posted_job',
    data: {
      jobOpening: jobTitle,
      title: jobTitle,
      department: tr('Engineering', 'الهندسة'),
      location: tr('Remote (Global)', 'عن بُعد (عالمي)'),
      employmentType: tr('Full-time', 'دوام كامل'),
      filterEnabled: true
    }
  };

  const starApplicant = {
    id: '_DEMO_APP_001',
    connectionKey: '_DEMO_CONN_001',
    name: tr('Sarah Johnson', 'سارة الخطيب'),
    email: 'sarah.j@example.com',
    phone: '+1 555-0101',
    applicationDate: now - day * 2,
    createdAt: now - day * 2,
    stageId: 'stage-1',
    substageId: 'filter-applicants',
    fitScore: 92,
    fitReasoning: tr('Excellent match with 6 years of React & Node.js experience. Strong background in cloud technologies.', 'تطابق ممتاز مع خبرة 6 سنوات في React وNode.js وخلفية قوية في تقنيات السحابة.'),
    experience: tr('6 years as Full Stack Developer', '6 سنوات كمطورة برمجيات شاملة'),
    skills: ['React', 'Node.js', 'AWS', 'TypeScript', 'MongoDB'],
    aiAnalysis: {
      summary: tr(
        'Sarah is an exceptional candidate with 6+ years of full-stack development experience. Her expertise in React, Node.js, and AWS directly aligns with your requirements. She has led multiple successful projects and demonstrates strong problem-solving abilities.',
        'سارة مرشحة مميزة بخبرة تتجاوز 6 سنوات في تطوير البرمجيات الشاملة. خبرتها في React وNode.js وAWS تتوافق مباشرة مع متطلباتكم، وقادت مشاريع ناجحة وتظهر قدرات قوية في حل المشكلات.'
      ),
      strengths: tr(
        [
          'Extensive React & Node.js experience',
          'Cloud architecture expertise',
          'Team leadership',
          'Agile methodologies'
        ].join('|'),
        [
          'خبرة واسعة في React وNode.js',
          'إتقان البنى السحابية',
          'قيادة الفرق',
          'منهجيات أجايل'
        ].join('|')
      ).split('|'),
      concerns: [tr('None identified - excellent fit', 'لا توجد ملاحظات - ملاءمة ممتازة')],
      recommendation: tr('Strongly recommend proceeding to interview', 'نوصي بشدة بالانتقال للمقابلة')
    },
    messages: [
      {
        id: 'msg1',
        sender: 'candidate',
        text: "Thank you for considering my application. I'm very excited about this opportunity!",
        timestamp: now - day,
        read: true
      },
      {
        id: 'msg2',
        sender: 'employer',
        text: 'Hi Sarah, we would love to schedule an interview. Are you available next week?',
        timestamp: now - 12 * 60 * 60 * 1000,
        read: true
      }
    ],
    teamMessages: [
      {
        id: 'tm1',
        sender: 'John (CTO)',
        text: tr("Strong technical background, let's move forward", 'خلفية تقنية قوية، لنمضِ قدمًا'),
        timestamp: now - 6 * 60 * 60 * 1000
      }
    ],
    teamScorecards: [
      {
        interviewer: 'John (CTO)',
        rating: 9,
        feedback: tr('Excellent technical skills and communication', 'مهارات تقنية ممتازة وتواصل واضح'),
        timestamp: now - 5 * 60 * 60 * 1000
      }
    ],
    scheduledInterviews: [
      {
        id: 'int_demo_1',
        title: 'Technical Interview',
        date: 'Tomorrow, 2:00 PM',
        interviewer: 'John (CTO)'
      }
    ],
    assessmentResults: {
      score: 9.2,
      totalQuestions: 10,
      analysis: 'Exceptional performance on technical assessment. Strong understanding of React patterns and Node.js best practices.',
      questionBreakdown: [
        {
          question: tr('Explain React Hooks', 'اشرح React Hooks'),
          score: 10,
          evaluation: tr('Perfect answer with practical examples', 'إجابة مثالية مع أمثلة عملية')
        },
        {
          question: tr('Async/Await vs Promises', 'Async/Await مقابل Promises'),
          score: 9,
          evaluation: tr('Clear explanation with minor details missing', 'شرح واضح مع بعض التفاصيل الناقصة')
        },
        {
          question: tr('Database Optimization', 'تحسين قواعد البيانات'),
          score: 9,
          evaluation: tr('Good understanding of indexing and query optimization', 'فهم جيد للفهرسة وتحسين الاستعلامات')
        }
      ]
    }
  };

  const otherFitApplicants = [
    {
      id: '_DEMO_APP_002',
      name: tr('Miguel Torres', 'ميغيل توريس'),
      email: 'miguel.t@example.com',
      applicationDate: now - day * 3,
      createdAt: now - day * 3,
      stageId: 'stage-1',
      substageId: 'filter-applicants',
      fitScore: 88,
      fitReasoning: tr('Full-stack engineer with AI/ML exposure and strong leadership skills.', 'مهندس برمجيات شامل مع خبرة في الذكاء الاصطناعي/التعلم الآلي ومهارات قيادة قوية.'),
      experience: tr('7 years in software engineering', '7 سنوات في هندسة البرمجيات'),
      skills: ['Python', 'React', 'AWS', 'GraphQL']
    },
    {
      id: '_DEMO_APP_003',
      name: tr('Priya Natarajan', 'برية ناتاراجان'),
      email: 'priya.n@example.com',
      applicationDate: now - day * 4,
      createdAt: now - day * 4,
      stageId: 'stage-1',
      substageId: 'filter-applicants',
      fitScore: 86,
      fitReasoning: tr('Product-focused engineer with strong TypeScript and design system experience.', 'مهندسة تركز على المنتج مع خبرة قوية في TypeScript وأنظمة التصميم.'),
      experience: tr('5 years in front-end and product engineering', '5 سنوات في هندسة الواجهة الأمامية والمنتج'),
      skills: ['TypeScript', 'React', 'Design Systems', 'Storybook']
    }
  ];

  const allApplicants = [
    {
      id: '_DEMO_APP_004',
      name: tr('Daniel Ibrahim', 'دانيال إبراهيم'),
      email: 'daniel.i@example.com',
      applicationDate: now - day * 5,
      createdAt: now - day * 5,
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      experience: tr('4 years in backend development', '4 سنوات في تطوير النظم الخلفية'),
      skills: ['Node.js', 'PostgreSQL', 'Docker']
    },
    {
      id: '_DEMO_APP_005',
      name: tr('Chloe Martin', 'كلوي مارتن'),
      email: 'chloe.m@example.com',
      applicationDate: now - day * 6,
      createdAt: now - day * 6,
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      experience: tr('3 years in QA automation', '3 سنوات في أتمتة الاختبارات'),
      skills: ['Cypress', 'Playwright', 'Jest']
    },
    {
      id: '_DEMO_APP_006',
      name: tr('Adewale Okafor', 'أديوالي أوكافور'),
      email: 'adewale.o@example.com',
      applicationDate: now - day * 7,
      createdAt: now - day * 7,
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      experience: tr('5 years in DevOps and cloud infrastructure', '5 سنوات في DevOps والبنى السحابية'),
      skills: ['AWS', 'Terraform', 'Kubernetes']
    },
    {
      id: '_DEMO_APP_007',
      name: tr('Emily Zhang', 'إميلي تشانغ'),
      email: 'emily.z@example.com',
      applicationDate: now - day * 8,
      createdAt: now - day * 8,
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      experience: tr('New grad Software Engineer', 'خريجة جديدة في هندسة البرمجيات'),
      skills: ['Python', 'React', 'Django']
    },
    {
      id: '_DEMO_APP_008',
      name: tr('Noah Patel', 'نوح باتيل'),
      email: 'noah.p@example.com',
      applicationDate: now - day * 9,
      createdAt: now - day * 9,
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      experience: tr('2 years in support engineering', 'سنتان في هندسة الدعم الفني'),
      skills: ['Customer Support', 'SQL', 'Salesforce']
    }
  ];

  return {
    job,
    applicants: [starApplicant, ...otherFitApplicants, ...allApplicants]
  };
};

const Applicants = () => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Use access control hook for trial/subscription check
  const { hasAccess, accessType, trial, subscription } = useAccessControl();

  // i18n hooks
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const tutorialInterviewSubstage = useMemo(() => ({
    ...DEMO_INTERVIEW_SUBSTAGE,
    title: isRTL ? 'المقابلة الأولى' : DEMO_INTERVIEW_SUBSTAGE.title,
    name: isRTL ? 'المقابلة الأولى' : DEMO_INTERVIEW_SUBSTAGE.title
  }), [isRTL]);
  const tutorialAssessmentSubstage = useMemo(() => ({
    ...DEMO_ASSESSMENT_SUBSTAGE,
    title: isRTL ? 'تقييم المهارات' : DEMO_ASSESSMENT_SUBSTAGE.title,
    name: isRTL ? 'تقييم المهارات' : DEMO_ASSESSMENT_SUBSTAGE.title
  }), [isRTL]);

  // Localize built-in workflow names for demo/default data
  const localizeStaticWorkflowNames = (workflow) => {
    if (!workflow) return workflow;
    const clone = cloneData(workflow);
    const staticNames = {
      'all-applicants-1': t('substages.allApplicants'),
      'filter-applicants': t('substages.fitApplicants'),
      'offer-sent': t('substages.offerSent'),
      'hired-applicants': t('substages.hired'),
      'move-to-onboarding': t('substages.moveToOnboarding')
    };
    Object.values(clone || {}).forEach(stage => {
      if (!stage?.substages) return;
      Object.entries(stage.substages).forEach(([sid, sub]) => {
        if (staticNames[sid]) {
          sub.name = staticNames[sid];
        }
      });
    });
    return clone;
  };

  // Tutorial state management
  const [tutorialActive, setTutorialActive] = useState(false);
  const [realApplicants, setRealApplicants] = useState([]); // Backup of real applicants
  const [realJobs, setRealJobs] = useState([]); // Backup of real jobs
  const [realWorkflow, setRealWorkflow] = useState({}); // Backup of real workflow
  const [driverInstance, setDriverInstance] = useState(null); // Store driver instance for cleanup
  const [showOnboardingFormTutorial, setShowOnboardingFormTutorial] = useState(false); // For tutorial

  // State management
  const [currActiveJob, setCurrActiveJob] = useState('');
  const [currActiveStage, setCurrActiveStage] = useState('stage-1');
  const [stageCounter, setStageCounter] = useState(2);
  const [sortedApplicantsData, setSortedApplicantsData] = useState({});
  const [jobs, setJobs] = useState([]);

  // Popup states
  const [addSubstagePopup, setAddSubstagePopup] = useState({ show: false, type: '' });
  const [filteringSubstagePopup, setFilteringSubstagePopup] = useState(false);
  const [applicantDetailsPopup, setApplicantDetailsPopup] = useState({ show: false, applicantId: '' });
  const handledMentionLinkRef = useRef('');
  const [subscriptionPopup, setSubscriptionPopup] = useState(false);

  // Other states
  const [selectedJobName, setSelectedJobName] = useState('');
  const [isFilterJobArea, setIsFilterJobArea] = useState(false);
  const [currentJobId, setCurrentJobId] = useState('');
  const [applicantsForJob, setApplicantsForJob] = useState([]);
  const [workflowStages, setWorkflowStages] = useState({});
  const [loading, setLoading] = useState(true);

  // Add state for dynamic substages
  const [dynamicSubstages, setDynamicSubstages] = useState({
    'stage-1': []
  });

  // Add state for filter substage
  const [filterSubstageEnabled, setFilterSubstageEnabled] = useState(false);

  // Add state for selected job data
  const [selectedJobData, setSelectedJobData] = useState(null);

  // Add state for AI assessment substages (for popup sections)
  const [aiAssessmentSubstages, setAiAssessmentSubstages] = useState([]);

  // Tutorial helpers
  const tutorialBackupRef = useRef(null);
  const tutorialJobTitleRef = useRef('');
  const tutorialJobIdRef = useRef('');
  const tutorialStepFlagsRef = useRef({ interview: false, assessment: false });
  const tutorialApplicantsRef = useRef([]);
  const tutorialCompletedRef = useRef(false);
  const tutorialCompletionActionRef = useRef(null);
  const tutorialSchedulerOpenedRef = useRef(false);
  const tutorialSchedulerAutoOpenDisabledRef = useRef(false);
  const [tutorialDemoApplicants, setTutorialDemoApplicants] = useState([]);

  // Add authentication state for collaborator support
  const [userUID, setUserUID] = useState('');
  const teamAccess = useTeamAccessProfile(userUID);
  const canManageApplicants = teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageApplicants;
  const accessibleJobs = useMemo(
    () => teamAccess.filterByDepartmentAccess(jobs, (option) => option?.data?.department || option?.department),
    [jobs, teamAccess]
  );

  // Global applicant search states
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [allApplicants, setAllApplicants] = useState([]);

  // Analytics dashboard state

  // Notifications state
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsInitialized, setNotificationsInitialized] = useState(false);
  const [notificationsCache] = useState(() => new NotificationsCache());
  const [isUserActive, setIsUserActive] = useState(true);

  // Pending approvals state (for approvers)
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const { isJobApprover, isOfferApprover } = useApprovalSettings(userUID);

	  const visiblePendingApprovals = useMemo(() => {
	    // If pendingApprovals is array
	    return pendingApprovals.filter(p => {
	      // Safe check for type
	      const type = p.type || p.metadata?.type;
	      const approvalDepartment = p.metadata?.department || p.metadata?.jobDepartment || p.snapshot?.department || '';
	      if (type === 'offer_letter') return isOfferApprover && teamAccess.canAccessDepartment(approvalDepartment);
	      if (type === 'job_posting') return isJobApprover && teamAccess.canAccessDepartment(approvalDepartment);
	      // Default to not showing if type unknown? Or show?
	      // Let's assume unknown types are hidden or safe.
	      // Actually, better to be safe.
	      return false;
	    });
	  }, [pendingApprovals, isJobApprover, isOfferApprover, teamAccess]);

  // Interview scheduler state
  const [showInterviewScheduler, setShowInterviewScheduler] = useState(false);
  const [schedulerApplicantId, setSchedulerApplicantId] = useState('');
  const [schedulerSubstageData, setSchedulerSubstageData] = useState(null);

  // Calendar connection prompt state
  const [showCalendarConnectionPrompt, setShowCalendarConnectionPrompt] = useState(false);

  // CV Upload Manager state
  const [showCVUploadManager, setShowCVUploadManager] = useState(false);

  // Multi-select state
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState({ startX: 0, startY: 0, endX: 0, endY: 0 });
  const mainContainerRef = useRef(null);
  const applicantsWorkspaceRef = useRef(null);

  // Update notification count whenever notifications or pending approvals change
  useEffect(() => {
    // Simplified count: just count notifications marked as unread + pending approvals
    const unreadCount = notifications.filter(n => n.unread === true).length + visiblePendingApprovals.length;
    setNotificationCount(unreadCount);
  }, [notifications, visiblePendingApprovals]);

  // Listen for pending approvals
  useEffect(() => {
    if (!userUID) return;

    const approvalsRef = ref(database, `companies/${userUID}/pending_approvals`);
    const unsubscribe = onValue(approvalsRef, (snapshot) => {
      if (snapshot.exists()) {
        const approvals = [];
        snapshot.forEach((child) => {
          const data = child.val();
          // Only show pending approvals
          if (data.status === 'pending') {
            approvals.push({ ...data, id: child.key });
          }
        });
        // Sort by submittedAt descending (newest first)
        approvals.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
        setPendingApprovals(approvals);
      } else {
        setPendingApprovals([]);
      }
    });

    return () => unsubscribe();
  }, [userUID]);

  useEffect(() => {
    tutorialApplicantsRef.current = tutorialDemoApplicants;
  }, [tutorialDemoApplicants]);

  // User activity detection
  useUserActivity(() => {
    setIsUserActive(true);
  }, 1000);

  // Function to load and listen for notifications
  const loadNotifications = async (authenticatedUID) => {
    try {
      // Only load notifications once per session
      if (notificationsInitialized) {
        return;
      }

      // Check cache first
      const cachedNotifications = notificationsCache.getCachedNotifications();
      if (cachedNotifications.length > 0) {
        setNotifications(cachedNotifications);
      }

      // Debounced notification processing - reduced delay for better responsiveness
      const debouncedProcessNotifications = debounce(async (connections) => {
        // Removed user activity check - notifications should always process
        const processedNotifications = await processNotificationsEfficiently(connections, authenticatedUID, t);
        setNotifications(processedNotifications);
        notificationsCache.setCachedNotifications(processedNotifications);
      }, 500); // Reduced from 2000ms to 500ms for faster response

      // Listen for all connections to detect unread chats
      const connectionsRef = ref(database, 'connections');
      onValue(connectionsRef, (snapshot) => {
        const connections = snapshot.val();
        if (connections) {
          debouncedProcessNotifications(connections);
        }
      });

      setNotificationsInitialized(true);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Function to check for unread chats
  const checkForUnreadChats = async (connections, authenticatedUID) => {
    const unreadChats = [];

    for (const [connectionId, connectionData] of Object.entries(connections)) {
      // Check if this connection belongs to the current user and has unread messages
      if (connectionId.startsWith(`${authenticatedUID}_`) && connectionData.newMsgEmpl === "true") {
        const applicantId = connectionId.split('_')[1];

        // Get applicant data
        try {
          const personRef = ref(database, `people/${applicantId}`);
          const personSnapshot = await get(personRef);
          const personData = personSnapshot.val();

          if (personData) {
            // Get the last message timestamp
            const messages = connectionData.messages || {};
            const lastMessageTimestamp = Math.max(...Object.values(messages).map(msg => msg.timestamp || 0));

            unreadChats.push({
              id: `chat_${connectionId}`,
              type: 'unread_chat',
              title: 'New Message',
              description: `You have a new message from ${personData.username}`,
              candidateName: personData.username,
              jobTitle: connectionData.jobAppliedFor || 'Unknown Position',
              jobId: connectionData.jobId || '',
              applicantId: applicantId,
              timestamp: lastMessageTimestamp,
              unread: true
            });
          }
        } catch (error) {
          console.error('Error getting person data for notification:', error);
        }
      }
    }

    // Update notifications state with unread chats
    setNotifications(prev => {
      const nonChatNotifications = prev.filter(n => n.type !== 'unread_chat');
      return [...nonChatNotifications, ...unreadChats];
    });
  };

  // Function to check for assessment completions
  const checkForAssessmentCompletions = async (connections, authenticatedUID) => {
    const completedAssessments = [];

    for (const [connectionId, connectionData] of Object.entries(connections)) {
      if (connectionId.startsWith(`${authenticatedUID}_`) && connectionData.assessments) {
        // CRITICAL FIX: Properly extract applicantId for both imported and authenticated candidates
        let applicantId, applicantType;
        if (connectionId.includes('_imported_')) {
          // Format: {employerUID}_imported_{applicantId}
          applicantId = connectionId.split('_imported_')[1];
          applicantType = 'imported';
        } else {
          // Format: {employerUID}_{applicantId}
          applicantId = connectionId.split('_')[1];
          applicantType = 'authenticated';
        }

        try {
          const personRef = ref(database, `people/${applicantId}`);
          const personSnapshot = await get(personRef);
          const personData = personSnapshot.val();

          if (personData) {
            for (const [assessmentId, assessmentData] of Object.entries(connectionData.assessments)) {
              // Check for assessments that are completed OR evaluated but haven't sent notification yet
              const isCompleted = assessmentData.response?.status === 'completed';
              const isEvaluated = ['evaluated', 'manual_review'].includes(assessmentData.response?.status);
              const notificationNotSent = !assessmentData.notificationSent || assessmentData.notificationSent === false;

              if ((isCompleted || isEvaluated) && notificationNotSent) {
                // CRITICAL FIX: Fetch assessment title from companies node, not connections!
                let assessmentTitle = 'an assessment';
                try {
                  const assessmentRef = ref(database, `companies/${authenticatedUID}/assessments/${assessmentId}`);
                  const assessmentSnapshot = await get(assessmentRef);
                  if (assessmentSnapshot.exists()) {
                    const assessmentDetails = assessmentSnapshot.val();
                    assessmentTitle = assessmentDetails.title || assessmentDetails.name || 'an assessment';
                  }
                } catch (error) {
                  console.warn('Could not fetch assessment title for notification:', error);
                }

                completedAssessments.push({
                  id: `assessment_${connectionId}_${assessmentId}`,
                  type: 'assessment_completed',
                  title: 'Assessment Completed',
                  description: `${personData.username || 'A candidate'} has completed ${assessmentTitle}`,
                  assessmentTitle,
                  candidateName: personData.username || 'Unknown Candidate',
                  jobTitle: connectionData.jobAppliedFor || 'Unknown Position',
                  jobId: connectionData.jobId || '',
                  applicantId: applicantId,
                  timestamp: assessmentData.response.submittedAt || Date.now(),
                  unread: true
                });
              }
            }
          }
        } catch (error) {
          console.error('Error getting person data for assessment notification:', error);
        }
      }
    }

    // Update notifications state with completed assessments
    setNotifications(prev => {
      const nonAssessmentNotifications = prev.filter(n => n.type !== 'assessment_completed');
      return [...nonAssessmentNotifications, ...completedAssessments];
    });
  };

  // Function to format time since last stage change
  const formatTimeSinceLastMove = (lastStageChange) => {
    if (!lastStageChange) return 'over a week';

    const now = new Date();
    const moveDate = new Date(lastStageChange);
    const diffInDays = Math.floor((now - moveDate) / (1000 * 60 * 60 * 24));

    if (diffInDays >= 30) {
      const months = Math.floor(diffInDays / 30);
      return months === 1 ? 'one month' : `${months} months`;
    } else if (diffInDays >= 14) {
      const weeks = Math.floor(diffInDays / 7);
      return weeks === 1 ? 'one week' : `${weeks} weeks`;
    } else if (diffInDays >= 7) {
      return 'one week';
    } else {
      return 'over a week';
    }
  };

  // Function to check for unmoved candidates
  const checkForUnmovedCandidates = async (connections, authenticatedUID) => {
    const unmovedCandidates = [];
    const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const now = Date.now();

    for (const [connectionId, connectionData] of Object.entries(connections)) {
      if (connectionId.startsWith(`${authenticatedUID}_`) && connectionData.stageId && connectionData.substageId) {
        const applicantId = connectionId.split('_')[1];

        // Skip if already hired or rejected
        if (connectionData.substageId === 'hired-applicants' || connectionData.substageId === 'rejected-applicants') {
          continue;
        }

        // Check if candidate has been in same position for more than a week
        const lastStageChange = connectionData.lastStageChange;
        let lastMoveTime = 0;

        if (lastStageChange) {
          lastMoveTime = new Date(lastStageChange).getTime();
        } else {
          // Fallback to applicationDate if lastStageChange doesn't exist
          lastMoveTime = connectionData.applicationDate || 0;
        }

        if (now - lastMoveTime > ONE_WEEK_MS) {
          try {
            const personRef = ref(database, `people/${applicantId}`);
            const personSnapshot = await get(personRef);
            const personData = personSnapshot.val();

            if (personData) {
              const timeSinceMove = formatTimeSinceLastMove(lastStageChange);

              unmovedCandidates.push({
                id: `unmoved_${connectionId}`,
                type: 'unmoved_candidate',
                title: 'Candidate Needs Attention',
                description: `${personData.username} has been in the same stage for ${timeSinceMove}`,
                durationDays: Math.floor((now - new Date(lastStageChange).getTime()) / 86400000),
                candidateName: personData.username,
                jobTitle: connectionData.jobAppliedFor || 'Unknown Position',
                jobId: connectionData.jobId || '',
                applicantId: applicantId,
                timestamp: now,
                unread: true
              });
            }
          } catch (error) {
            console.error('Error getting person data for unmoved candidate notification:', error);
          }
        }
      }
    }

    // Update notifications state with unmoved candidates
    setNotifications(prev => {
      const nonUnmovedNotifications = prev.filter(n => n.type !== 'unmoved_candidate');
      // Filter out duplicates based on applicantId
      const filteredUnmovedCandidates = unmovedCandidates.filter(newNotif =>
        !prev.some(existingNotif =>
          existingNotif.type === 'unmoved_candidate' && existingNotif.applicantId === newNotif.applicantId
        )
      );
      return [...nonUnmovedNotifications, ...filteredUnmovedCandidates];
    });
  };

  // Helper function to get icon class based on substage type (from vanilla JS)
  const getIconClass = (substageType) => {
    const iconClassMap = {
      'filtering': 'fa-solid fa-filter',
      'interview': 'fa-solid fa-calendar-day',
      'assessment': 'fa-solid fa-gears',
      'ai-assessment': 'fa-solid fa-robot',
      'ai-interview': 'fa-solid fa-video'
    };
    return iconClassMap[substageType] || 'fa-solid fa-people-group';
  };

  // Authenticate user - check if they're a company owner or collaborator (matching vanilla JS)
  const authenticateUser = async () => {
    return new Promise(async (resolve, reject) => {
      if (!currentUser) {
        reject('No current user');
        return;
      }

      try {
        // First check if user is a company owner
        const companySnapshot = await get(ref(database, `companies/${currentUser.uid}`));
        if (companySnapshot.exists()) {
          resolve(currentUser.uid);
          return;
        }

        // If not a company owner, check if they're a collaborator
        const collabSnapshot = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnapshot.exists()) {
          const collaboratorData = collabSnapshot.val();
          const collaboratorCompanyUID = collaboratorData.companyId || collaboratorData.company_uid;
          if (collaboratorCompanyUID) {
            resolve(collaboratorCompanyUID);
            return;
          }
        }

        // If neither company owner nor collaborator, reject
        console.log('User is neither a company owner nor a collaborator');
        reject('User not authenticated');
      } catch (error) {
        console.error('Authentication error:', error);
        reject('Authentication failed');
      }
    });
  };

  // Initialize component when user is available
  useEffect(() => {
    if (currentUser) {
      initializeComponent();
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedJobName && !accessibleJobs.some((option) => option.title === selectedJobName)) {
      setSelectedJobName('');
      setCurrActiveJob('');
      setCurrentJobId('');
      setSelectedJobData(null);
      setApplicantsForJob([]);
    }
  }, [accessibleJobs, selectedJobName]);

  // Set up history management
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    const handlePopState = () => {
      window.history.go(1);
    };
    window.onpopstate = handlePopState;

    return () => {
      window.onpopstate = null;
    };
  }, []);

  // Initialize component
  const initializeComponent = async () => {
    try {
      setLoading(true);

      // First authenticate the user (check if company owner or collaborator)
      const authenticatedUID = await authenticateUser();
      setUserUID(authenticatedUID);
      console.log('User authenticated with company UID:', authenticatedUID);

      // ✅ No longer need to check subscription manually - useAccessControl handles it
      await loadWorkflow(authenticatedUID);
      await loadAllConnectionsAndUpdateDropdown(authenticatedUID);
      await loadNotifications(authenticatedUID);
      console.log('Component initialized successfully');
    } catch (error) {
      console.error('Error initializing component:', error);
      // If authentication fails, redirect to home
      window.location.href = "https://www.jobseeqr.co";
    } finally {
      setLoading(false);
    }
  };

  // Load filter substage
  const loadFilterSubstage = async (authenticatedUID) => {
    try {
      const companyUID = authenticatedUID || userUID;

      // Check if user has active access (subscription or trial) - if so, enable by default
      if (hasAccess) {
        setFilterSubstageEnabled(true);
        // Add filter substage to dynamic substages for stage-1
        setDynamicSubstages(prev => ({
          ...prev,
          'stage-1': [
            {
              id: 'filter-applicants',
              title: t('substages.fitApplicants'),
              icon: 'fa-solid fa-star',
              type: 'filtering'
            },
            ...prev['stage-1']
          ]
        }));

        // Enable filter in database if not already enabled
        const filterFlagRef = ref(database, `companies/${companyUID}/filterSubstageEnabled`);
        const flagSnapshot = await get(filterFlagRef);
        if (!flagSnapshot.val()) {
          await set(filterFlagRef, true);
        }
      } else {
        // Check database for existing setting if no subscription
        const filterFlagRef = ref(database, `companies/${companyUID}/filterSubstageEnabled`);
        const flagSnapshot = await get(filterFlagRef);

        if (flagSnapshot.val()) {
          setFilterSubstageEnabled(true);
          // Add filter substage to dynamic substages for stage-1
          setDynamicSubstages(prev => ({
            ...prev,
            'stage-1': [
              {
                id: 'filter-applicants',
                title: t('substages.fitApplicants'),
                icon: 'fa-solid fa-star',
                type: 'filtering'
              },
              ...prev['stage-1']
            ]
          }));
        } else {
          setFilterSubstageEnabled(false);
        }
      }
    } catch (error) {
      console.error('Error loading filter substage:', error);
    }
  };

  // Load workflow from Firebase
  const loadWorkflow = async (authenticatedUID) => {
    console.log("Loading the workflow for you.");
    try {
      const companyUID = authenticatedUID || userUID;
      const workflowRef = ref(database, `companies/${companyUID}/hiringWorkflow`);
      const snapshot = await get(workflowRef);
      const stages = snapshot.val();

      if (!stages) {
        console.log("No workflow found for employer:", companyUID);
        // Set default workflow
        setStageCounter(4);
        setWorkflowStages({
          'stage-1': { substages: {} }
        });
        // Load filter substage for stage-1
        await loadFilterSubstage(authenticatedUID);
        return;
      }

      const totalStages = Object.keys(stages).length;
      setStageCounter(totalStages + 1);
      setWorkflowStages(stages);

      // Load dynamic substages from workflow
      const dynamicSubstagesData = {
        'stage-1': []
      };

      // Extract AI assessment substages for popup sections
      const aiAssessments = [];

      Object.keys(stages).forEach(stageId => {
        if (stages[stageId].substages) {
          const substages = Object.keys(stages[stageId].substages).map(substageId => {
            const substage = stages[stageId].substages[substageId];
            const localizedName = substageId === 'filter-applicants' ? t('substages.fitApplicants') : substage.name;

            // Only company-owned AI assessment definitions have response and
            // evaluation records that the candidate popup can load safely.
            if (AI_EDITABLE_SUBSTAGE_TYPES.has(substage.type)) {
              aiAssessments.push({
                id: substageId,
                title: substage.name,
                stageId: stageId,
                assessmentId: substage.link,
                ...substage
              });
            }

            return {
              id: substageId,
              title: localizedName,
              icon: getIconClass(substage.type),
              type: substage.type,
              afterId: substage.afterId || null,
              departments: substage.departments || null,
              ...substage
            };
          });
          dynamicSubstagesData[stageId] = substages;
        }
      });

      setDynamicSubstages(dynamicSubstagesData);
      setAiAssessmentSubstages(aiAssessments);

      // Load filter substage for stage-1
      await loadFilterSubstage(authenticatedUID);
    } catch (error) {
      console.error('Error loading workflow:', error);
    }
  };

  // Load all connections and update dropdown - OPTIMIZED VERSION
  const loadAllConnectionsAndUpdateDropdown = async (authenticatedUID) => {
    const companyUID = authenticatedUID || userUID;

    try {

      // 🚀 OPTIMIZED: Load all job options (posted jobs + custom lists) using new function
      const jobOptions = await loadAllJobOptions(companyUID);
      const accessibleJobOptions = teamAccess.filterByDepartmentAccess(jobOptions, (option) => option?.data?.department || option?.department);
      setJobs(jobOptions);

      // 🚀 OPTIMIZED: Use new indexed structure instead of scanning all connections
      const { sortedData, allApplicants, needsMigration } = await loadEmployerApplicantsOptimized(companyUID);

      // If the index is missing, run the migration script
      if (needsMigration) {
        console.log('Applicant index not found, starting data migration...');
        await migrateToOptimizedStructure(companyUID);
        // Reload data after migration
        const { sortedData, allApplicants } = await loadEmployerApplicantsOptimized(companyUID);
        setSortedApplicantsData(sortedData);
        setAllApplicants(allApplicants);
      } else {
        setSortedApplicantsData(sortedData);
        setAllApplicants(allApplicants);
      }

      // Set applicants for current job if one is selected
      console.log('loadAllConnectionsAndUpdateDropdown completed, checking current job:', currActiveJob);
      console.log('Available jobs in sortedData:', Object.keys(sortedData));

      if (currActiveJob && sortedData[currActiveJob]) {
        console.log('Refreshing applicants for current job:', currActiveJob);
        // Find the job option to get the ID
        const selectedOption = accessibleJobOptions.find(option => option.title === currActiveJob);
        if (selectedOption) {
          await displayApplicantsForJob(sortedData[currActiveJob], currActiveJob, selectedOption.id);
        } else {
          await displayApplicantsForJob(sortedData[currActiveJob]);
        }
      } else {
        console.log('No current job or no data for current job, clearing applicants');
        setApplicantsForJob([]);
      }

    } catch (error) {
      console.error('Error loading connections optimized:', error);

      // Fallback: Try to migrate data if index doesn't exist
      try {
        console.log('Attempting to migrate to optimized structure...');
        await migrateToOptimizedStructure(companyUID);

        // Retry with optimized structure
        const { sortedData, allApplicants } = await loadEmployerApplicantsOptimized(companyUID);
        setSortedApplicantsData(sortedData);
        setAllApplicants(allApplicants);
      } catch (migrationError) {
        console.error('Migration failed, falling back to legacy method:', migrationError);
        // Could implement legacy fallback here if needed
        setSortedApplicantsData({});
        setApplicantsForJob([]);
        setAllApplicants([]);
      }
    }
  };

  // NOTE: sortApplicantsIntoJobs function removed - replaced by optimized indexing

  // Display applicants for job - OPTIMIZED VERSION
  const displayApplicantsForJob = async (connectionKeys, jobTitle = null, jobId = null) => {
    console.log('displayApplicantsForJob called with:', connectionKeys, 'for job:', jobTitle, 'ID:', jobId);

    if (!connectionKeys || connectionKeys.length === 0) {
      console.log("No applicants to display.");
      setApplicantsForJob([]);
      return;
    }

    const companyUID = userUID;

    try {
      // Use provided jobTitle and jobId if available, otherwise fall back to state
      let targetJobId = jobId;
      let targetJobTitle = jobTitle;

      if (!targetJobId || !targetJobTitle) {
        // Fallback: Find which job/list this is for by checking our jobs state
        const selectedOption = accessibleJobs.find(option => option.title === (targetJobTitle || currActiveJob));
        console.log('Looking for job with title:', targetJobTitle || currActiveJob, 'in jobs:', accessibleJobs.map(j => j.title));

        if (selectedOption) {
          targetJobId = selectedOption.id;
          targetJobTitle = selectedOption.title;
          console.log('Found target job ID:', targetJobId);
        }
      }

      if (targetJobId) {
        // Use optimized loading with cached data
        console.log('Using optimized loading for job ID:', targetJobId, 'title:', targetJobTitle);
        const applicants = await loadJobApplicantsOptimized(companyUID, targetJobId, connectionKeys);
        console.log('Loaded applicants:', applicants.length);
        setApplicantsForJob(applicants);
      } else {
        // Fallback: Load individual connections (less optimal but still works)
        console.log('Using fallback loading method');
        const applicants = [];
        for (const connectionKey of connectionKeys) {
          try {
            const connectionRef = ref(database, `connections/${connectionKey}`);
            const connectionSnapshot = await get(connectionRef);

            if (connectionSnapshot.exists()) {
              const connectionData = connectionSnapshot.val();
              const { applicantId, applicantType } = parseConnectionKey(connectionKey);

              let applicantName = 'Unknown Name';

              if (applicantType === 'authenticated') {
                const nameRef = ref(database, `people/${applicantId}/username`);
                const nameSnapshot = await get(nameRef);
                applicantName = nameSnapshot.val() || 'Unknown Name';
              } else {
                let profileRef = ref(database, `imported_profiles/${companyUID}/${applicantId}`);
                let profileSnapshot = await get(profileRef);

                if (!profileSnapshot.exists()) {
                  profileRef = ref(database, `imported_profiles/${applicantId}`);
                  profileSnapshot = await get(profileRef);
                }

                if (profileSnapshot.exists()) {
                  applicantName = profileSnapshot.val().name || 'Unknown Name';
                }
              }

              applicants.push({
                id: applicantId,
                connectionKey,
                name: applicantName,
                applicantType,
                stageId: connectionData.stageId || 'stage-1',
                substageId: connectionData.substageId || 'all-applicants-1',
                ...connectionData
              });
            }
          } catch (error) {
            console.warn(`Error loading connection ${connectionKey}:`, error);
          }
        }
        console.log('Fallback loaded applicants:', applicants.length);
        setApplicantsForJob(applicants);
      }
    } catch (error) {
      console.error('Error loading applicants optimized:', error);
      setApplicantsForJob([]);
    }
  };

  // Select job - OPTIMIZED VERSION  
  const selectJob = async (jobName) => {
    console.log('Selecting job:', jobName);

    // Clear current applicants first to prevent showing stale data
    setApplicantsForJob([]);

    setCurrActiveJob(jobName);
    setSelectedJobName(jobName);

    // Find job/list option by title
    const selectedOption = accessibleJobs.find(option => option.title === jobName);
    if (selectedOption) {
      setCurrentJobId(selectedOption.id);
      setSelectedJobData(selectedOption); // Store the full job/list data

      // Check if filtering is enabled (only for posted jobs, custom lists don't have this)
      if (selectedOption.type === 'posted_job') {
        const jobData = selectedOption.data || selectedOption;
        if (jobData.filterEnabled === true) {
          setIsFilterJobArea(false);
        } else {
          setIsFilterJobArea(true);
        }
      } else if (selectedOption.type === 'custom_list') {
        // Custom lists don't have filtering in the same way
        setIsFilterJobArea(false);
      }

      // 🚀 ENHANCED: Always refresh data to ensure we have the latest applicants
      console.log('Refreshing data before loading applicants for job:', jobName);

      try {
        // Force reload applicant data to ensure we have the latest
        const { sortedData, allApplicants } = await loadEmployerApplicantsOptimized(userUID);
        setSortedApplicantsData(sortedData);
        setAllApplicants(allApplicants);

        console.log('Updated sortedApplicantsData, looking for job:', jobName);
        console.log('Available jobs after refresh:', Object.keys(sortedData));

        if (sortedData[jobName]) {
          console.log('Found applicants for job after refresh, loading...');
          await displayApplicantsForJob(sortedData[jobName], jobName, selectedOption.id);
        } else {
          console.log('No applicants found for job after refresh:', jobName);
          setApplicantsForJob([]);
        }
      } catch (error) {
        console.error('Error refreshing data for job selection:', error);
        // Fallback to cached data
        if (sortedApplicantsData[jobName]) {
          console.log('Using cached data as fallback');
          await displayApplicantsForJob(sortedApplicantsData[jobName], jobName, selectedOption.id);
        } else {
          setApplicantsForJob([]);
        }
      }
    } else {
      console.log('No job option found with title:', jobName);
      setApplicantsForJob([]);
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const applicantId = query.get('applicantId') || '';
    const initialTab = query.get('tab') || '';
    if (!applicantId || initialTab !== 'team' || !userUID || !accessibleJobs.length) return;

    const targetKey = location.search;
    if (handledMentionLinkRef.current === targetKey) return;
    handledMentionLinkRef.current = targetKey;

    let cancelled = false;
    const openMentionedApplicant = async () => {
      const jobId = query.get('jobId') || '';
      const jobTitle = query.get('jobTitle') || '';
      const targetJob = accessibleJobs.find((job) => (
        (jobId && String(job.id) === jobId) || (jobTitle && job.title === jobTitle)
      ));
      if (targetJob) await selectJob(targetJob.title);
      if (cancelled) return;
      setApplicantDetailsPopup({
        show: true,
        applicantId,
        connectionKey: query.get('connectionKey') || '',
        initialTab: 'team',
      });
      navigate(location.pathname, { replace: true });
    };
    openMentionedApplicant().catch((error) => {
      handledMentionLinkRef.current = '';
      console.error('Could not open mentioned candidate note:', error);
    });
    return () => { cancelled = true; };
  // The URL is the durable handoff from Home and email; job data is only a readiness gate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, accessibleJobs.length, userUID]);

  // Toggle stage
  const toggleStage = (selectedStageId) => {
    setCurrActiveStage(selectedStageId);
  };

  // Add filtering to job
  const addFilteringToJob = async () => {
    if (currentJobId && selectedJobData) {
      try {
        const companyUID = userUID;
        // Only enable filtering for posted jobs, not custom lists
        if (selectedJobData.type === 'posted_job') {
          const jobPostRef = ref(database, `companies/${companyUID}/posts/${currentJobId}`);
          await update(jobPostRef, { filterEnabled: true });
          setIsFilterJobArea(false);
        }
      } catch (error) {
        console.error('Error enabling filtering:', error);
      }
    }
  };

  // Add filtering layer to workflow
  const addFilteringLayer = async () => {
    try {
      const companyUID = userUID;
      const filterRef = ref(database, `companies/${companyUID}/filterSubstageEnabled`);
      await set(filterRef, true);

      // Update state and UI immediately
      setFilterSubstageEnabled(true);
      setDynamicSubstages(prev => ({
        ...prev,
        'stage-1': [
          {
            id: 'filter-applicants',
            title: t('substages.fitApplicants'),
            icon: 'fa-solid fa-star',
            type: 'filtering'
          },
          ...prev['stage-1']
        ]
      }));

      // Close the popup
      setFilteringSubstagePopup(false);
    } catch (error) {
      console.error('Error adding filter substage:', error);
    }
  };

  // Remove filter substage
  const removeFilterSubstage = async () => {
    try {
      const companyUID = userUID;

      // Remove all filter-enabled flags from jobs
      const jobsWithFilterRef = ref(database, `companies/${companyUID}/posts`);
      const snapshot = await get(jobsWithFilterRef);

      if (snapshot.exists()) {
        const updates = {};
        Object.keys(snapshot.val()).forEach(jobId => {
          const job = snapshot.val()[jobId];
          if (job.filterEnabled) {
            updates[`companies/${companyUID}/posts/${jobId}/filterEnabled`] = null;
          }
        });

        if (Object.keys(updates).length > 0) {
          await update(ref(database), updates);
        }
      }

      // Remove master flag from Firebase
      await set(ref(database, `companies/${companyUID}/filterSubstageEnabled`), null);

      // Update UI
      setFilterSubstageEnabled(false);
      setDynamicSubstages(prev => ({
        ...prev,
        'stage-1': prev['stage-1'].filter(substage => substage.id !== 'filter-applicants')
      }));

      console.log('Filter substage removed from the workflow.');
    } catch (error) {
      console.error('Error removing filter substage:', error);
    }
  };

  // Drag and drop handlers
  const allowDrop = (event) => {
    event.preventDefault();
  };

  const handleDragStart = (event, applicantId) => {
    event.dataTransfer.setData("text/plain", applicantId);
  };

  // Note: Drag and drop logic is handled in ApplicantsWorkspace.js

  // Update applicant position in Firebase
  const ensureAiInterviewQuestionsReady = async (companyId, assessmentId, applicantId, connectionKey) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_PROD}/generate-candidate-ai-interview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: companyId,
        assessment_id: assessmentId,
        applicant_id: applicantId,
        connection_key: connectionKey
      })
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.detail || data?.error || 'Failed to generate AI interview questions for this candidate');
    }

    return data.assessment;
  };

  const sendAssessmentInvite = async (companyId, applicantId, assessmentLink, assessmentTitle, applicantName, companyName, applicantType = 'authenticated', assessmentId = '', inviteType = 'assessment') => {
    try {
      // Get applicant email based on type
      let applicantEmail = null;
      if (applicantType === 'imported') {
        let importedEmailRef = ref(database, `imported_profiles/${companyId}/${applicantId}/email`);
        let importedEmailSnapshot = await get(importedEmailRef);

        if (!importedEmailSnapshot.exists()) {
          importedEmailRef = ref(database, `imported_profiles/${applicantId}/email`);
          importedEmailSnapshot = await get(importedEmailRef);
        }

        applicantEmail = importedEmailSnapshot.val();
      } else {
        const applicantEmailRef = ref(database, `people/${applicantId}/email`);
        const emailSnapshot = await get(applicantEmailRef);
        applicantEmail = emailSnapshot.val();
      }

      if (!applicantEmail) {
        console.error("Applicant email not found, cannot send invite.");
        return;
      }

      // Resolve job title from connection data
      let connectionKey = `${companyId}_${applicantId}`;
      try {
        const importedKey = `${companyId}_imported_${applicantId}`;
        const importedSnap = await get(ref(database, `connections/${importedKey}`));
        if (importedSnap.exists()) {
          connectionKey = importedKey;
        }
      } catch (e) { }

      let jobTitle = '';
      let jobId = '';
      try {
        const connSnap = await get(ref(database, `connections/${connectionKey}`));
        const connData = connSnap.val() || {};
        jobTitle = connData.jobAppliedFor || connData.jobTitle || '';
        jobId = connData.jobId || '';
      } catch (e) {
        jobTitle = '';
        jobId = '';
      }

      // Send via backend with SendGrid template styling and correct job title
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
      const emailResponse = await fetch(`${BACKEND_URL}/send-assessment-invite-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicant_email: applicantEmail,
          applicant_name: applicantName,
          company_name: companyName,
          assessment_title: assessmentTitle,
          assessment_link: assessmentLink,
          job_title: jobTitle || undefined,
          invite_type: inviteType
        })
      });

      if (!emailResponse.ok) {
        throw new Error('Failed to send assessment invite via backend.');
      }
      console.log(`Assessment invite email sent via backend to ${applicantEmail}`);

      // Send a message in Firebase Realtime Database (only for authenticated users who can access chat)
      if (applicantType === 'authenticated') {
        const messagesRef = ref(database, `connections/${connectionKey}/messages`);
        const newMessageRef = push(messagesRef);
        const inviteLabel = inviteType === 'interview' ? 'interview' : 'assessment';
        await set(newMessageRef, {
          message: inviteType === 'interview'
            ? `You have been invited to complete the AI interview: "${assessmentTitle}". You will respond to each question in video format. Please use the following link to begin: ${assessmentLink}`
            : `You have been invited to take the assessment: "${assessmentTitle}". Please use the following link to begin: ${assessmentLink}`,
          user: companyId,
          timestamp: new Date().toISOString(),
          type: 'assessment_invite',
        });
        await update(ref(database, `connections/${connectionKey}`), { newMsgJS: "true" });

        console.log(`${inviteLabel} invite message sent to Firebase for applicant ${applicantId}.`);
      }

      emitAtsAnalyticsEvent({
        company_uid: companyId,
        event_type: 'assessment_invited',
        subject: {
          applicantId,
          connectionKey,
          applicantType
        },
        job: {
          jobId,
          jobTitle: jobTitle || ''
        },
        context: {
          assessmentId: assessmentId || '',
          assessmentTitle,
          assessmentType: inviteType === 'interview'
            ? 'ai_interview'
            : (assessmentLink?.includes('/take-assessment/') ? 'ai_assessment' : 'external_assessment')
        }
      });

    } catch (error) {
      console.error('Error sending assessment invite:', error);
      alert(uiText('An error occurred while sending the assessment invite. Please check the console and try again.'));
    }
  };

  // LEGACY WRAPPER: Convert to new optimized system
  const updateApplicantPositionInFirebase = async (applicantId, newStageId, newSubstageId, jobTitle = '') => {
    try {
      const companyUID = userUID;

      // 🚀 OPTIMIZED: Generate proper connection key based on applicant type
      // First, try to find the connection key from our current applicants data
      let connectionKey = null;
      const currentApplicant = applicantsForJob.find(app => app.id === applicantId);

      if (currentApplicant && currentApplicant.connectionKey) {
        connectionKey = currentApplicant.connectionKey;
      } else {
        // Fallback: Try both authenticated and imported connection patterns
        connectionKey = `${companyUID}_${applicantId}`;

        // Check if this is an imported applicant
        try {
          const importedConnectionKey = `${companyUID}_imported_${applicantId}`;
          const importedConnectionRef = ref(database, `connections/${importedConnectionKey}`);
          const importedConnectionSnapshot = await get(importedConnectionRef);
          if (importedConnectionSnapshot.exists()) {
            connectionKey = importedConnectionKey;
          }
        } catch (error) {
          console.warn('Could not check for imported connection, using authenticated pattern');
        }
      }

      const additionalData = {
        accepted: true
      };
      const currentJobSignals = selectedJobData?.data || selectedJobData || {};
      additionalData.candidateId = currentApplicant?.candidateId || applicantId;
      additionalData.roleId = currentApplicant?.roleId || currentJobSignals?.roleId || currentJobSignals?.id || currentJobId || '';
      additionalData.jobTitle = jobTitle || currentApplicant?.jobTitle || currentJobSignals?.jobOpening || currentJobSignals?.title || '';
      additionalData.department = currentApplicant?.department || currentJobSignals?.department || '';
      additionalData.location = currentApplicant?.location || currentJobSignals?.companyLocation || currentJobSignals?.location || '';
      additionalData.ownerRecruiterId = currentApplicant?.ownerRecruiterId || currentJobSignals?.ownerRecruiterId || currentUser?.uid || '';
      additionalData.ownerRecruiterEmail = currentApplicant?.ownerRecruiterEmail || currentJobSignals?.ownerRecruiterEmail || currentUser?.email || '';
      additionalData.actorUserId = currentUser?.uid || '';
      additionalData.actorUserEmail = currentUser?.email || '';
      additionalData.primarySource = currentApplicant?.primarySource || 'JOBSEEQR';
      additionalData.sourceCategory = currentApplicant?.sourceCategory || 'platform';
      additionalData.sourceTags = currentApplicant?.sourceTags || [];

      if (newSubstageId === 'hired-applicants') {
        additionalData.hired = true;
        additionalData.hiredDate = new Date().toISOString();

        // Check if this job is linked to a manpower request
        // If so, notify the original requester that their request has been fulfilled
        try {
          const jobId = currentApplicant?.jobId || currentJobId || selectedJobData?.id;
          if (jobId) {
            const jobRef = ref(database, `companies/${companyUID}/posts/${jobId}`);
            const jobSnap = await get(jobRef);
            const jobData = jobSnap.val();

            if (jobData?.sourceManpowerRequestId) {
              console.log('Hire detected for job linked to manpower request:', jobData.sourceManpowerRequestId);

              // Update the manpower request with hired candidate info
              await update(ref(database,
                `companies/${companyUID}/manpower_requests/${jobData.sourceManpowerRequestId}`), {
                status: 'fulfilled',
                fulfilledAt: Date.now(),
                hiredCandidateId: applicantId,
                fulfillmentNotes: 'Candidate hired through ATS'
              });

              // Get requester email and send notification
              const requestRef = ref(database,
                `companies/${companyUID}/manpower_requests/${jobData.sourceManpowerRequestId}`);
              const requestSnap = await get(requestRef);
              const requestData = requestSnap.val();

              if (requestData?.submitter?.email) {
                // Call backend to send hire notification email
                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
                if (BACKEND_URL) {
                  fetch(`${BACKEND_URL}/manpower/notify-hire`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      company_uid: companyUID,
                      request_id: jobData.sourceManpowerRequestId,
                      requester_email: requestData.submitter.email,
                      requester_name: requestData.submitter.name,
                      job_title: requestData.position?.title || jobData.jobOpening || 'the position',
                      hired_candidate_name: currentApplicant?.name || 'a candidate'
                    })
                  }).catch(err => console.error('Error sending hire notification:', err));
                }
              }
            }
          }
        } catch (err) {
          console.error('Error checking for manpower request link:', err);
        }
      }

      // Use optimized update function
      const result = await updateApplicantPositionOptimized(connectionKey, newStageId, newSubstageId, additionalData);
      console.log('Applicant position updated successfully (optimized)');

      // Check if the applicant was moved to an assessment substage (both ai-assessment and external assessment)
      const substageData = workflowStages[newStageId]?.substages?.[newSubstageId];
      if (substageData && ASSESSMENT_SUBSTAGE_TYPES.has(substageData.type) && substageData.link) {
        let assessmentLink = '';

        // Get applicant name and type
        let applicantName = 'Candidate';
        const { applicantType } = parseConnectionKey(connectionKey);

        if (applicantType === 'authenticated') {
          const applicantNameSnap = await get(ref(database, `people/${applicantId}/username`));
          applicantName = applicantNameSnap.val() || 'Candidate';
        } else {
          let profileSnap = await get(ref(database, `imported_profiles/${companyUID}/${applicantId}/name`));
          if (!profileSnap.exists()) {
            profileSnap = await get(ref(database, `imported_profiles/${applicantId}/name`));
          }
          applicantName = profileSnap.val() || 'Candidate';
        }

        const companyNameSnap = await get(ref(database, `companies/${companyUID}/name`));
        const companyName = companyNameSnap.val() || 'the hiring team';

        // Handle based on assessment type
        if (AI_EDITABLE_SUBSTAGE_TYPES.has(substageData.type)) {
          // For AI assessments, create encrypted JOBSEEQR link
          const assessmentId = substageData.link;
          const assessmentSnap = await get(ref(database, `companies/${companyUID}/assessments/${assessmentId}`));
          const assessmentData = assessmentSnap.exists() ? assessmentSnap.val() : null;

          if (substageData.type === 'ai-interview' && assessmentData?.baseConfig?.generationMode === 'ai') {
            await ensureAiInterviewQuestionsReady(companyUID, assessmentId, applicantId, connectionKey);
          }

          const plainTextSlug = `${companyUID}_${assessmentId}_${applicantId}`;
          const encryptedSlug = encryptData(plainTextSlug);

          if (encryptedSlug) {
            assessmentLink = `${window.location.origin}/take-assessment/${encryptedSlug}`;
            await sendAssessmentInvite(
              companyUID,
              applicantId,
              assessmentLink,
              substageData.name || substageData.title,
              applicantName,
              companyName,
              applicantType,
              assessmentId,
              substageData.type === 'ai-interview' ? 'interview' : 'assessment'
            );
          } else {
            console.error("Failed to encrypt assessment slug. Invite not sent.");
            alert(uiText("Could not create a secure link for the assessment. Please check your encryption key setup."));
          }
        } else if (substageData.type === 'assessment') {
          // For external assessments, use the external link directly
          assessmentLink = substageData.link;
          console.log(`[Applicants.js] Sending external assessment invite:`, {
            applicantId,
            applicantName,
            assessmentLink,
            assessmentTitle: substageData.name || substageData.title
          });
          await sendAssessmentInvite(companyUID, applicantId, assessmentLink, substageData.name || substageData.title, applicantName, companyName, applicantType, substageData.link, 'assessment');
          console.log(`✓ External assessment invite sent successfully with link: ${assessmentLink}`);
        }
      }

    } catch (error) {
      console.error('Error updating applicant position (optimized):', error);
    }
  };

  // Check and prompt for link
  const checkAndPromptForLink = async (applicantId, stageId, substageId) => {
    if (!CALENDAR_ENABLED) return; // feature disabled
    try {
      const companyUID = userUID;
      const substageRef = ref(database, `companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`);
      const snapshot = await get(substageRef);
      const substageData = snapshot.val();

      if (substageData && substageData.type === 'interview') {
        // Store substage data for the InterviewScheduler
        setSchedulerSubstageData(substageData);

        // Check if user has calendar integration
        const calendarRef = ref(database, `companies/${companyUID}/calendarIntegrations`);
        const calendarSnapshot = await get(calendarRef);

        if (calendarSnapshot.exists() && Object.keys(calendarSnapshot.val()).length > 0) {
          // User has calendar integration, show scheduler directly
          setSchedulerApplicantId(applicantId);
          setShowInterviewScheduler(true);
        } else {
          // No calendar integration, show attractive calendar connection popup
          setShowCalendarConnectionPrompt(true);
          setSchedulerApplicantId(applicantId); // Store for later use
        }
      } else if (substageData && ASSESSMENT_SUBSTAGE_TYPES.has(substageData.type)) {
        // Assessment links are automatically sent via email when candidates are dragged/dropped
        // No additional prompting needed since external assessment link is already configured
        console.log('Assessment substage detected - link will be sent automatically on drop');
      }
    } catch (error) {
      console.error('Error checking for link prompt:', error);
    }
  };

  // Show promo handler
  const showPromo = () => {
    setSubscriptionPopup(true);
  };

  // Tutorial system
  const loadTutorial = () => {
    if (window.Driver) {
      // Tutorial implementation would go here
      console.log('Loading tutorial...');
    }
  };

  // Popup handlers
  const openAddSubstagePopup = (type) => {
    setAddSubstagePopup({ show: true, type });
  };

  const closeAddSubstagePopup = () => {
    setAddSubstagePopup({ show: false, type: '' });
  };

  // Function to handle when a new substage is added
  const handleSubstageAdded = (stageId, substageData) => {
    const formattedSubstage = {
      ...substageData,
      title: substageData.name,
      icon: getIconClass(substageData.type)
    };

    setDynamicSubstages(prev => {
      const current = [...prev[stageId]];
      if (formattedSubstage.afterId) {
        const idx = current.findIndex(s => s.id === formattedSubstage.afterId);
        if (idx !== -1) {
          current.splice(idx + 1, 0, formattedSubstage);
        } else {
          current.push(formattedSubstage);
        }
      } else {
        current.push(formattedSubstage);
      }
      return {
        ...prev,
        [stageId]: current
      };
    });
  };

  // Function to handle when a substage is updated
  const handleSubstageUpdated = (substageId, newName) => {
    setDynamicSubstages(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(stageId => {
        updated[stageId] = updated[stageId].map(substage =>
          substage.id === substageId
            ? { ...substage, title: newName, name: newName }
            : substage
        );
      });
      return updated;
    });
  };

  // Function to handle when a substage is deleted
  const handleSubstageDeleted = (substageId) => {
    setDynamicSubstages(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(stageId => {
        updated[stageId] = updated[stageId].filter(substage => substage.id !== substageId);
      });
      return updated;
    });
  };

  // Get applicants to show with proper default handling
  const getApplicantsToShow = () => {
    // Return the properly structured applicant data with names
    return applicantsForJob || [];
  };

  // Get jobs to show - only jobs/lists that have applicants
  const getJobsToShow = () => {
    // Filter job options to only show those that have applicants
    return accessibleJobs.filter(option => sortedApplicantsData[option.title]);
  };

  // Get selected job to show
  const getSelectedJobToShow = () => {
    return selectedJobName;
  };

  const openFilteringSubstagePopup = () => {
    setFilteringSubstagePopup(true);
  };

  const closeFilteringSubstagePopup = () => {
    setFilteringSubstagePopup(false);
  };

  const openApplicantPopup = (applicant) => {
    const applicantId = typeof applicant === 'string' ? applicant : applicant.id;
    const connectionKey = typeof applicant === 'string' ? null : (applicant.connectionKey || null);

    // Mark first open: write firstOpenedAt if not already set
    const targetApplicant = applicantsForJob.find(a => a.id === applicantId);
    if (targetApplicant && !targetApplicant.firstOpenedAt && currentJobId && targetApplicant.connectionKey) {
      const now = Date.now();
      const companyUID = userUID;
      const indexPath = `companies/${companyUID}/applicant_index/by_job/${currentJobId}/${targetApplicant.connectionKey}/firstOpenedAt`;
      update(ref(database, `companies/${companyUID}/applicant_index/by_job/${currentJobId}/${targetApplicant.connectionKey}`), { firstOpenedAt: now }).catch(() => {});
      setApplicantsForJob(prev => prev.map(a => a.id === applicantId ? { ...a, firstOpenedAt: now } : a));
    }

    if (typeof applicant === 'string') {
      setApplicantDetailsPopup({ show: true, applicantId: applicant });
      return;
    }
    setApplicantDetailsPopup({ show: true, applicantId: applicant.id, connectionKey: connectionKey || '' });
  };

  const closeApplicantPopup = () => {
    setApplicantDetailsPopup({ show: false, applicantId: '' });
  };

  // Handle when an applicant is removed (for rejection)
  const handleApplicantRemoved = (applicantId) => {
    // Remove applicant from the UI immediately
    setApplicantsForJob(prev => prev.filter(applicant => applicant.id !== applicantId));

    // Also update sortedApplicantsData if needed
    if (currActiveJob && sortedApplicantsData[currActiveJob]) {
      setSortedApplicantsData(prev => ({
        ...prev,
        [currActiveJob]: prev[currActiveJob].filter(id => id !== applicantId)
      }));
    }

    console.log('Applicant removed from UI:', applicantId);
  };

  // Handle when multiple applicants are removed (for reject all)
  const handleMultipleApplicantsRemoved = (applicantIds) => {
    // Remove applicants from the UI immediately
    setApplicantsForJob(prev => prev.filter(applicant => !applicantIds.includes(applicant.id)));

    // Also update sortedApplicantsData if needed
    if (currActiveJob && sortedApplicantsData[currActiveJob]) {
      setSortedApplicantsData(prev => ({
        ...prev,
        [currActiveJob]: prev[currActiveJob].filter(id => !applicantIds.includes(id))
      }));
    }

    // Update allApplicants for global search
    setAllApplicants(prev => prev.filter(applicant => !applicantIds.includes(applicant.id)));

    console.log('Multiple applicants removed from UI:', applicantIds);
  };

  const closeSubscriptionPopup = () => {
    setSubscriptionPopup(false);
  };

  const callHiredNotificationAndEmail = async (applicantId, employerId) => {
    try {
      // This would typically call a Firebase Cloud Function or external API
      console.log(`Sending hired notification for applicant ${applicantId} from employer ${employerId}`);
      // Implementation would depend on your notification system
    } catch (error) {
      console.error('Error sending hired notification:', error);
    }
  };

  // Global search helpers
  const openGlobalSearch = () => setIsGlobalSearchOpen(true);
  const closeGlobalSearch = () => setIsGlobalSearchOpen(false);

  const handleGlobalApplicantSelect = async (applicantObj) => {
    try {
      await selectJob(applicantObj.jobTitle);
      openApplicantPopup(applicantObj.id);
    } catch (err) {
      console.error('Error selecting applicant from global search:', err);
    } finally {
      closeGlobalSearch();
    }
  };

  // Keyboard shortcut (Cmd/Ctrl + K) to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openGlobalSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openNotifications = () => setIsNotificationsOpen(true);
  const closeNotifications = () => setIsNotificationsOpen(false);

  const handleNotificationSelect = async (notification) => {
    // Mark notification as read in UI
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, unread: false } : n)
    );

    // Update notification count
    setNotificationCount(prev => Math.max(0, prev - 1));

    // Handle specific notification types - mark as read in database
    try {
      if (notification.type === 'assessment_completed') {
        // CRITICAL FIX: Use correct connection key pattern for both imported and authenticated candidates
        let connectionId;
        if (notification.applicantId.startsWith('temp_')) {
          // Format: {employerUID}_imported_{applicantId}
          connectionId = `${userUID}_imported_${notification.applicantId}`;
        } else {
          // Format: {employerUID}_{applicantId}
          connectionId = `${userUID}_${notification.applicantId}`;
        }

        // Fix: Extract assessment ID correctly from notification ID format: assessment_${connectionId}_${assessmentId}
        const notificationIdParts = notification.id.split('_');
        const assessmentId = notificationIdParts[notificationIdParts.length - 1]; // Get the last part as assessment ID
        const assessmentRef = ref(database, `connections/${connectionId}/assessments/${assessmentId}`);
        await update(assessmentRef, { notificationSent: true });

      } else if (notification.type === 'unread_chat') {
        // CRITICAL FIX: Use correct connection key pattern for both imported and authenticated candidates
        let connectionId;
        if (notification.applicantId.startsWith('temp_')) {
          // Format: {employerUID}_imported_{applicantId}
          connectionId = `${userUID}_imported_${notification.applicantId}`;
        } else {
          // Format: {employerUID}_{applicantId}
          connectionId = `${userUID}_${notification.applicantId}`;
        }
        const connectionRef = ref(database, `connections/${connectionId}`);
        await update(connectionRef, { newMsgEmpl: "false" });

      } else if (notification.type === 'unmoved_candidate') {
        // CRITICAL FIX: Use correct connection key pattern for both imported and authenticated candidates
        let connectionId;
        if (notification.applicantId.startsWith('temp_')) {
          // Format: {employerUID}_imported_{applicantId}
          connectionId = `${userUID}_imported_${notification.applicantId}`;
        } else {
          // Format: {employerUID}_{applicantId}
          connectionId = `${userUID}_${notification.applicantId}`;
        }
        const connectionRef = ref(database, `connections/${connectionId}`);
        await update(connectionRef, {
          unmovedNotificationDismissed: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating notification status in database:', error);
    }

    // Close notifications popup
    closeNotifications();

    // Navigate to candidate like global search does
    const applicantObj = {
      id: notification.applicantId,
      name: notification.candidateName,
      jobTitle: notification.jobTitle,
      jobId: notification.jobId
    };

    await handleGlobalApplicantSelect(applicantObj);
  };

  // Interview scheduler functions
  const closeInterviewScheduler = () => {
    setShowInterviewScheduler(false);
    setSchedulerApplicantId('');
  };

  const handleViewApprovalDetails = (approval) => {
    closeNotifications();
    if (approval.type === 'offer_letter') {
      if (applicantsWorkspaceRef.current && applicantsWorkspaceRef.current.openOfferReview) {
        applicantsWorkspaceRef.current.openOfferReview(approval);
      }
    }
  };

  const handleInterviewScheduled = (scheduleData) => {
    // Refresh status badges so "interview scheduled" appears immediately
    if (applicantsWorkspaceRef.current?.refreshStatuses) {
      applicantsWorkspaceRef.current.refreshStatuses();
    }
  };

  // Calendar connection prompt handlers
  const closeCalendarConnectionPrompt = () => {
    setShowCalendarConnectionPrompt(false);
    setSchedulerApplicantId('');
  };

  const handleConnectCalendar = () => {
    // Redirect to extensions page to connect calendar
    window.location.href = '/external-apps#calendar';
  };

  const handleScheduleManually = () => {
    // Close the prompt and show the scheduler (which will show the no-calendar warning)
    setShowCalendarConnectionPrompt(false);
    setShowInterviewScheduler(true);
  };

  // Get candidate name for the prompt
  const getCandidateName = (applicantId) => {
    const applicant = getApplicantsToShow().find(a => a.id === applicantId);
    return applicant?.name || applicant?.applicantName || 'this candidate';
  };

  // Multi-select handlers
  const handleMouseDown = (e) => {
    // Only start selection if clicking on background areas, not on interactive elements
    if (e.target.closest('.applicant-cell') ||
      e.target.closest('.substage-hdr') ||
      e.target.closest('.stage-section-hdr') ||
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('a') ||
      e.target.closest('.dropdown-content') ||
      e.target.closest('i') ||
      e.target.closest('.list-view-container') ||
      e.target.closest('.applicants-list-table') ||
      e.target.closest('table') ||
      e.target.closest('select') ||
      e.target.closest('td') ||
      e.target.closest('th')) {
      return;
    }

    const rect = mainContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    e.preventDefault();

    setIsSelecting(true);
    setSelectionBox({
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
      endX: e.clientX - rect.left,
      endY: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isSelecting) return;

    const rect = mainContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    setSelectionBox(prev => ({
      ...prev,
      endX: currentX,
      endY: currentY
    }));

    // Find all applicant cells that intersect with the selection box
    const applicantElements = document.querySelectorAll('.applicant-cell');
    const selected = [];

    applicantElements.forEach(element => {
      const elemRect = element.getBoundingClientRect();
      const elemRelative = {
        left: elemRect.left - rect.left,
        top: elemRect.top - rect.top,
        right: elemRect.right - rect.left,
        bottom: elemRect.bottom - rect.top
      };

      // Calculate selection box bounds
      const selectionLeft = Math.min(selectionBox.startX, currentX);
      const selectionRight = Math.max(selectionBox.startX, currentX);
      const selectionTop = Math.min(selectionBox.startY, currentY);
      const selectionBottom = Math.max(selectionBox.startY, currentY);

      // Check if element intersects with selection box
      const intersects = !(
        elemRelative.right < selectionLeft ||
        elemRelative.left > selectionRight ||
        elemRelative.bottom < selectionTop ||
        elemRelative.top > selectionBottom
      );

      if (intersects) {
        const applicantId = element.getAttribute('data-applicant-id');
        if (applicantId) {
          selected.push(applicantId);
        }
      }
    });

    setSelectedApplicants(selected);
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setSelectionBox({ startX: 0, startY: 0, endX: 0, endY: 0 });
  };

  // Clear selection when Escape is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedApplicants([]);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // ==================== TUTORIAL IMPLEMENTATION ====================

  // Generate demo data for ATS tutorial


  // Cleanup demo data and restore real data
  const clearTutorialHighlights = () => {
    document.querySelectorAll('.tutorial-glow').forEach(element => {
      element.classList.remove('tutorial-glow');
    });
  };

  const cleanupATSDemoData = () => {
    setTutorialActive(false);
    setShowInterviewScheduler(false);
    setSchedulerApplicantId('');
    setSchedulerSubstageData(null);
    setShowOnboardingFormTutorial(false);
    setTutorialDemoApplicants([]);
    clearTutorialHighlights();
    applicantsWorkspaceRef.current?.closeOnboardingModal?.();

    if (tutorialBackupRef.current) {
      const {
        applicants,
        jobs: backupJobs,
        workflow,
        dynamicSubstages: backupDynamicSubstages,
        filterEnabled,
        sortedApplicants,
        selectedJobName: backupJobName,
        selectedJobData: backupJobData,
        currActiveJob: backupCurrActiveJob,
        currentJobId: backupCurrentJobId,
        isFilterJobArea: backupIsFilterJobArea,
        allApplicants: backupAllApplicants
      } = tutorialBackupRef.current;

      setApplicantsForJob(applicants);
      setJobs(backupJobs);
      setWorkflowStages(workflow);
      setDynamicSubstages(backupDynamicSubstages);
      setFilterSubstageEnabled(filterEnabled);
      setSortedApplicantsData(sortedApplicants);
      setSelectedJobName(backupJobName);
      setSelectedJobData(backupJobData);
      setCurrActiveJob(backupCurrActiveJob);
      setCurrentJobId(backupCurrentJobId);
      setIsFilterJobArea(backupIsFilterJobArea);
      setAllApplicants(backupAllApplicants);
    } else {
      setApplicantsForJob(realApplicants);
      setJobs(realJobs);
      setWorkflowStages(realWorkflow);
      setSelectedJobName('');
      setCurrActiveJob('');
    }

    setApplicantDetailsPopup({ show: false, applicantId: '' });
    setAddSubstagePopup({ show: false, type: '' });
    tutorialBackupRef.current = null;
    tutorialJobTitleRef.current = '';
    tutorialJobIdRef.current = '';
    tutorialStepFlagsRef.current = { interview: false, assessment: false };
    tutorialSchedulerOpenedRef.current = false;
    tutorialSchedulerAutoOpenDisabledRef.current = false;
    setDriverInstance(null);
    console.log('✅ ATS demo data cleaned up, real data restored');
  };

  // Helper function to wait for an element to exist in the DOM
  const waitForElement = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkElement = () => {
        const element = document.querySelector(selector);
        if (element) {
          console.log(`✅ Found element: ${selector}`);
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          console.error(`❌ Element ${selector} not found within ${timeout}ms`);
          reject(new Error(`Element ${selector} not found within ${timeout}ms`));
        } else {
          setTimeout(checkElement, 50);
        }
      };
      checkElement();
    });
  };

  const waitForElementRemoval = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkRemoval = () => {
        const element = document.querySelector(selector);
        if (!element) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          console.error(`❌ Element ${selector} still present after ${timeout}ms`);
          reject(new Error(`Element ${selector} still present after ${timeout}ms`));
        } else {
          setTimeout(checkRemoval, 50);
        }
      };
      checkRemoval();
    });
  };

  const startATSTutorial = () => {
    if (tutorialActive) {
      return;
    }

    tutorialCompletedRef.current = false;
    tutorialCompletionActionRef.current = null;
    tutorialSchedulerOpenedRef.current = false;
    tutorialSchedulerAutoOpenDisabledRef.current = false;
    setTutorialActive(true);
    clearTutorialHighlights();

    tutorialBackupRef.current = {
      applicants: cloneData(applicantsForJob),
      jobs: cloneData(jobs),
      workflow: cloneData(workflowStages),
      dynamicSubstages: cloneData(dynamicSubstages),
      filterEnabled: filterSubstageEnabled,
      sortedApplicants: cloneData(sortedApplicantsData),
      selectedJobName,
      selectedJobData,
      currActiveJob,
      currentJobId,
      isFilterJobArea,
      allApplicants: cloneData(allApplicants)
    };

    setRealApplicants(cloneData(applicantsForJob));
    setRealJobs(cloneData(jobs));
    setRealWorkflow(cloneData(workflowStages));

    const { job, applicants } = createAtsTutorialDataset(isRTL);
    tutorialJobTitleRef.current = job.title;
    tutorialJobIdRef.current = job.id;
    tutorialStepFlagsRef.current = { interview: false, assessment: false };

    setSelectedJobName(job.title);
    setSelectedJobData(job);
    setCurrentJobId(job.id);
    setJobs([job]);
    setCurrActiveJob('');
    setIsFilterJobArea(false);
    setWorkflowStages(localizeStaticWorkflowNames(baseTutorialWorkflow()));
    setDynamicSubstages({
      'stage-1': [{
        id: 'filter-applicants',
        title: t('substages.fitApplicants'),
        icon: 'fa-solid fa-star',
        type: 'filtering'
      }],
    });
    setFilterSubstageEnabled(true);

    const demoApplicantsClone = cloneData(applicants);
    let currentTutorialApplicants = demoApplicantsClone;

    setTutorialDemoApplicants(demoApplicantsClone);
    setSortedApplicantsData({ [job.title]: cloneData(applicants) });
    setAllApplicants(cloneData(applicants));
    setApplicantsForJob([]);
    setApplicantDetailsPopup({ show: false, applicantId: '' });
    setShowInterviewScheduler(false);
    setSchedulerApplicantId('');
    setSchedulerSubstageData(null);
    setShowOnboardingFormTutorial(false);

    const syncApplicantsState = (nextApplicants) => {
      const cloned = cloneData(nextApplicants);
      currentTutorialApplicants = cloned;
      setTutorialDemoApplicants(cloned);
      setApplicantsForJob(cloned);
      setSortedApplicantsData(prev => ({
        ...(prev || {}),
        [tutorialJobTitleRef.current || job.title]: cloneData(cloned)
      }));
      setAllApplicants(cloned);
    };

    const revealTutorialApplicants = () => {
      syncApplicantsState(currentTutorialApplicants);
      setCurrActiveJob(job.title);
      setSelectedJobName(job.title);
    };

    const ensureTutorialSubstage = (substageConfig, flagKey) => {
      if (flagKey && tutorialStepFlagsRef.current[flagKey]) {
        return;
      }

      setDynamicSubstages(prev => {
        const stageOne = prev?.['stage-1'] || [];
        if (stageOne.some(step => step.id === substageConfig.id)) {
          return prev;
        }
        return {
          ...prev,
          'stage-1': [...stageOne, substageConfig]
        };
      });

      setWorkflowStages(prev => {
        const base = prev ? cloneData(prev) : localizeStaticWorkflowNames(baseTutorialWorkflow());
        if (!base['stage-1']) {
          base['stage-1'] = localizeStaticWorkflowNames(baseTutorialWorkflow())['stage-1'];
        }
        base['stage-1'].substages = {
          ...(base['stage-1'].substages || {}),
          [substageConfig.id]: {
            id: substageConfig.id,
            name: substageConfig.title,
            type: substageConfig.type,
            icon: substageConfig.icon
          }
        };
        return base;
      });

      if (flagKey) {
        tutorialStepFlagsRef.current[flagKey] = true;
      }
    };

    const moveDemoApplicant = (applicantId, substageId) => {
      const next = currentTutorialApplicants.map(app =>
        app.id === applicantId ? { ...app, substageId } : app
      );
      syncApplicantsState(next);
    };

    const ensureApplicantPosition = (applicantId, substageId) => {
      const latestApplicants = tutorialApplicantsRef.current || [];
      const current = latestApplicants.find(app => app.id === applicantId);
      if (!current || current.substageId !== substageId) {
        moveDemoApplicant(applicantId, substageId);
      }
    };

    const focusCandidateInSubstage = (substageId, applicantId = '_DEMO_APP_001') => {
      waitForElement(`[data-substage-id="${substageId}"]`, 2000)
        .then(() => {
          scrollSubstageIntoView(substageId);
          scrollApplicantIntoView(substageId, applicantId);
        })
        .catch(() => {
          scrollSubstageIntoView(substageId);
          scrollApplicantIntoView(substageId, applicantId);
        });
    };

    const reopenApplicantPopup = () => {
      setApplicantDetailsPopup({ show: true, applicantId: '_DEMO_APP_001' });
    };

    const openScheduler = () => {
      tutorialSchedulerAutoOpenDisabledRef.current = false;
      tutorialSchedulerOpenedRef.current = true;
      setSchedulerApplicantId('_DEMO_APP_001');
      setSchedulerSubstageData({
        ...tutorialInterviewSubstage,
        name: tutorialInterviewSubstage.title,
        title: tutorialInterviewSubstage.title
      });
      setShowInterviewScheduler(true);
    };

    const closeScheduler = () => {
      setShowInterviewScheduler(false);
      setShowCalendarConnectionPrompt(false);
      setSchedulerApplicantId('');
      setSchedulerSubstageData(null);
      tutorialSchedulerOpenedRef.current = false;
    };

    const ensureSchedulerClosed = async () => {
      closeScheduler();
      try {
        await waitForElementRemoval('[data-tutorial="interview-scheduler-modal"]', 4000);
      } catch (error) {
        const leftoverModal = document.querySelector('[data-tutorial="interview-scheduler-modal"]');
        if (leftoverModal?.parentNode) {
          leftoverModal.parentNode.removeChild(leftoverModal);
        }
        const leftoverContent = document.querySelector('[data-tutorial="interview-scheduler-content"]');
        if (leftoverContent?.parentNode) {
          leftoverContent.parentNode.removeChild(leftoverContent);
        }
      } finally {
        tutorialSchedulerOpenedRef.current = false;
      }
    };

    const getAddStepButton = () =>
      document.querySelector('[data-tutorial="add-step-button"]') ||
      document.querySelector('.add-step-button') ||
      document.querySelector('.post-job-cta');

    const focusAddStepButton = (button) => {
      if (!button) {
        return;
      }
      const board = document.querySelector('[data-tutorial="workflow-board"]');
      if (board && board.contains(button)) {
        scrollElementIntoBoard(button);
      } else {
        button.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    };

    const clickAddStepButton = () => {
      const button = getAddStepButton();
      if (button) {
        focusAddStepButton(button);
        button.click();
        return true;
      }
      return false;
    };

    const closeAddStepDropdown = () => {
      const dropdown = document.querySelector('[data-tutorial="add-step-dropdown"]');
      if (dropdown) {
        const button = getAddStepButton();
        if (button) {
          button.click();
        } else {
          document.body.click();
        }
      }
    };

    let driverObj;

    const toggleGlow = (selectors, enable = true) => {
      const list = Array.isArray(selectors) ? selectors : [selectors];
      list.forEach((selector) => {
        if (!selector) return;
        document.querySelectorAll(selector).forEach(node => {
          node.classList.toggle('tutorial-glow', enable);
        });
      });
    };

    const highlightPrimarySubstages = (enable) => toggleGlow(['#all-applicants-1', '[data-substage-id="all-applicants-1"]', '#filter-applicants', '[data-substage-id="filter-applicants"]'], enable);
    const highlightNewlyAddedSteps = (enable) => toggleGlow([`[data-tutorial-substage="${DEMO_INTERVIEW_SUBSTAGE.id}"]`, `[data-tutorial-substage="${DEMO_ASSESSMENT_SUBSTAGE.id}"]`], enable);
    const highlightAddStepButton = (enable) => toggleGlow(['[data-tutorial="add-step-button"]', '.add-step-button', '.post-job-cta'], enable);
    const highlightSubstageArea = (substageId, enable) => toggleGlow([`#${substageId}`, `[data-substage-id="${substageId}"]`], enable);
    const highlightSubstageCandidate = (substageId, applicantId, enable) => toggleGlow([`[data-substage-id="${substageId}"] .applicant-cell[data-applicant-id="${applicantId}"]`], enable);
    const highlightOnboardingModal = (enable) => toggleGlow('[data-tutorial="onboarding-modal"]', enable);

    const getWorkflowBoard = () => document.querySelector('[data-tutorial="workflow-board"]');

    const scrollElementIntoBoard = (element) => {
      const board = getWorkflowBoard();
      if (!board || !element) {
        return;
      }
      requestAnimationFrame(() => {
        const boardRect = board.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const desiredLeft = board.scrollLeft + (elementRect.left - boardRect.left) - (boardRect.width / 2) + (elementRect.width / 2);
        const targetLeft = Math.max(desiredLeft, 0);
        board.scrollTo({ left: targetLeft, behavior: 'smooth' });
      });
    };

    const scrollSubstageIntoView = (substageIdOrSelector) => {
      const selector = substageIdOrSelector.startsWith('#') || substageIdOrSelector.startsWith('[')
        ? substageIdOrSelector
        : `[data-substage-id="${substageIdOrSelector}"]`;
      const element = document.querySelector(selector);
      if (element) {
        scrollElementIntoBoard(element);
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        });
      }
      return element;
    };

    const scrollApplicantIntoView = (substageId, applicantId) => {
      const selector = `[data-substage-id="${substageId}"] .applicant-cell[data-applicant-id="${applicantId}"]`;
      const element = document.querySelector(selector);
      if (element) {
        scrollElementIntoBoard(element);
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        });
      }
      return element;
    };

    const tr = (en, ar) => (isRTL ? ar : en);
    const steps = [
      {
        popover: {
          title: tr('🚀 Welcome to JOBSEEQR ATS', '🚀 أهلاً بك في نظام تتبع المتقدمين JOBSEEQR'),
          description: tr(
            `
          <strong>Your hiring command center.</strong><br/><br/>
          • Centralize every candidate in one workspace<br/>
          • Let AI highlight the strongest fits instantly<br/>
          • Layer in interviews, assessments, and team reviews<br/>
          • Collaborate with hiring managers in real time<br/><br/>
          Let's guide one candidate all the way to "Hired".
        `,
            `
          <strong>غرفة تحكم التوظيف لديك.</strong><br/><br/>
          • جمع كل المرشحين في مساحة عمل واحدة<br/>
          • دع الذكاء الاصطناعي يبرز الأنسب فورًا<br/>
          • أضف المقابلات والتقييمات ومراجعات الفريق<br/>
          • تعاون مع مديري التوظيف لحظيًا<br/><br/>
          سنقود مرشحًا واحدًا حتى خانة "تم التعيين".
        `
          )
        }
      },
      {
        element: () => document.querySelector('[data-tutorial="job-selector"]'),
        popover: {
          title: tr('📋 Select a Job', '📋 اختر الوظيفة'),
          description: tr(
            `
          Pick the role you want to review.<br/><br/>
          <em>We preloaded "${job.title}" with eight sample applicants.</em>
        `,
            `
          اختر الوظيفة التي تريد مراجعتها.<br/><br/>
          <em>قمنا بتحميل "${job.title}" بثمانية مرشحين تجريبيين.</em>
        `
          ),
          side: 'bottom',
          align: 'start',
          onNextClick: () => {
            revealTutorialApplicants();
            waitForElement('.applicant-cell, [data-applicant-id]')
              .then(() => driverObj.moveNext())
              .catch(error => {
                console.error('Applicant cells not found:', error);
                driverObj.moveNext();
              });
          }
        }
      },
      {
        element: () => document.querySelector('[data-tutorial="ats-workspace"]'),
        popover: {
          title: tr('🎯 Your Hiring Workspace', '🎯 مساحة عمل التوظيف'),
          description: tr(
            `
          One streamlined workflow with three views:<br/><br/>
          • <strong>Board:</strong> Drag candidates between substages<br/>
          • <strong>List:</strong> Filter and sort with spreadsheet speed<br/>
          • <strong>Gallery:</strong> Visual tiles for quick scanning<br/><br/>
          We've preloaded <strong>5 applicants</strong> under All Applicants and <strong>3 AI-qualified fits</strong> under Fit Applicants so you can see both lanes in action.
        `,
            `
          سير عمل موحّد بثلاثة عروض:<br/><br/>
          • <strong>اللوحة:</strong> اسحب المرشحين بين المراحل الفرعية<br/>
          • <strong>القائمة:</strong> فرز وتصفية بسرعة جدول البيانات<br/>
          • <strong>المعرض:</strong> بطاقات مرئية للمعاينة السريعة<br/><br/>
          حمّلنا مسبقًا <strong>5 مرشحين</strong> في جميع المتقدمين و<strong>3 مرشحين مؤهلين بالذكاء الاصطناعي</strong> في المتقدمين المناسبين لرؤية المسارين.
        `
          ),
          side: 'right',
          align: 'center'
        }
      },
      {
        element: () => {
          highlightPrimarySubstages(true);
          scrollSubstageIntoView('filter-applicants');
          return document.querySelector('[data-substage-id="filter-applicants"]') || document.querySelector('#all-applicants-1');
        },
        popover: {
          title: tr('🤖 All vs Fit Applicants', '🤖 جميع المتقدمين مقابل الأنسب'),
          description: tr(
            `
          • <strong>All Applicants (5):</strong> Everyone who applied<br/>
          • <strong>Fit Applicants (3):</strong> JOBSEEQR AI ranked the best matches<br/><br/>
          Both lanes stay in sync automatically as AI keeps screening new candidates for you.
        `,
            `
          • <strong>جميع المتقدمين (5):</strong> كل من قدّم على الوظيفة<br/>
          • <strong>المتقدمون الأنسب (3):</strong> الذكاء الاصطناعي صنّف أفضل التطابقات<br/><br/>
          المساران يتزامنان تلقائيًا مع استمرار فرز المرشحين الجدد.
        `
          ),
          side: 'right',
          align: 'center'
        },
        onNextClick: () => highlightPrimarySubstages(false),
        onPrevClick: () => highlightPrimarySubstages(false)
      },
      {
        element: () => {
          const button = getAddStepButton();
          highlightAddStepButton(true);
          focusAddStepButton(button);
          return button;
        },
        popover: {
          title: uiText('➕ Add Interview or Assessment Steps'),
          description: uiText(`
          Extend the workflow for this job:<br/><br/>
          • Interview rounds per department or seniority<br/>
          • AI-generated or external assessments<br/>
          • Department-specific steps when required<br/><br/>
          Let's open the step menu.
        `),
          side: 'left',
          align: 'start',
          onNextClick: () => {
            highlightAddStepButton(false);
            const clicked = clickAddStepButton();
            if (!clicked) {
              driverObj.moveNext();
              return;
            }
            waitForElement('[data-tutorial="add-step-dropdown"]')
              .then(() => driverObj.moveNext())
              .catch(error => {
                console.error('Add step dropdown not found:', error);
                driverObj.moveNext();
              });
          }
        },
        onPrevClick: () => {
          highlightAddStepButton(false);
        }
      },
      {
        element: '[data-tutorial="add-step-dropdown"]',
        popover: {
          title: uiText('⚙️ Configure New Steps'),
          description: uiText(`
          Pick what you need:<br/><br/>
          • <strong>Interview:</strong> Phone screen, panel, onsite—you choose<br/>
          • <strong>Assessment:</strong> Bring your own or auto-generate with JOBSEEQR AI<br/><br/>
          Every team can build the process that fits them.
        `),
          side: 'right',
          align: 'center',
          onNextClick: () => {
            closeAddStepDropdown();
            ensureTutorialSubstage(tutorialInterviewSubstage, 'interview');
            ensureTutorialSubstage(tutorialAssessmentSubstage, 'assessment');
            Promise.all([
              waitForElement(`[data-tutorial-substage="${DEMO_INTERVIEW_SUBSTAGE.id}"]`),
              waitForElement(`[data-tutorial-substage="${DEMO_ASSESSMENT_SUBSTAGE.id}"]`)
            ])
              .then(() => {
                scrollSubstageIntoView(DEMO_INTERVIEW_SUBSTAGE.id);
                scrollSubstageIntoView(DEMO_ASSESSMENT_SUBSTAGE.id);
                setTimeout(() => driverObj.moveNext(), 150);
              })
              .catch(error => {
                console.error('New tutorial steps not ready in time:', error);
                driverObj.moveNext();
              });
          },
          onPrevClick: () => {
            closeAddStepDropdown();
            setTimeout(() => driverObj.movePrevious(), 200);
          }
        }
      },
      {
        element: () => {
          highlightNewlyAddedSteps(true);
          const interviewNode = document.querySelector(`[data-tutorial-substage="${DEMO_INTERVIEW_SUBSTAGE.id}"]`);
          const assessmentNode = document.querySelector(`[data-tutorial-substage="${DEMO_ASSESSMENT_SUBSTAGE.id}"]`);
          if (interviewNode) {
            scrollElementIntoBoard(interviewNode);
          }
          if (assessmentNode) {
            scrollElementIntoBoard(assessmentNode);
          }
          return interviewNode || assessmentNode;
        },
        popover: {
          title: uiText('🧱 Example Steps Added'),
          description: uiText("For the sake of our example we added:<br/><br/>\u2022 <strong>{{interview}}</strong> \u2014 inline scheduling with AI suggestions<br/>\u2022 <strong>{{assessment}}</strong> \u2014 send an AI-built or external assessment<br/><br/>Rename, reorder, or delete these at any time.", { interview: tutorialInterviewSubstage.title, assessment: tutorialAssessmentSubstage.title }),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => highlightNewlyAddedSteps(false),
        onPrevClick: () => highlightNewlyAddedSteps(false)
      },
      {
        element: () => {
          return scrollApplicantIntoView('filter-applicants', '_DEMO_APP_001') ||
            document.querySelector('[data-substage-id="filter-applicants"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]');
        },
        popover: {
          title: uiText('👤 AI-Qualified Candidate'),
          description: uiText(`
          Let's click one of the candidates the AI flagged as a great fit.<br/><br/>
          • Fit score & context<br/>
          • Contact details<br/>
          • Quick actions to advance them
        `),
          side: 'top',
          align: 'start',
          onNextClick: () => {
            setApplicantDetailsPopup({ show: true, applicantId: '_DEMO_APP_001' });
            requestAnimationFrame(() => driverObj.moveNext());
          }
        }
      },
      {
        element: '.applicant-details-popup, .popup-content',
        popover: {
          title: uiText('📄 Deep Candidate Profile'),
          description: uiText(`
          Everything in one place:<br/><br/>
          • AI analysis & scorecards<br/>
          • Interview schedule & feedback<br/>
          • Assessment results with question-by-question breakdown<br/>
          • Messaging with the candidate and your team<br/><br/>
          Let's move Sarah to her interview.
        `),
          side: 'left',
          align: 'center',
          onNextClick: () => {
            setApplicantDetailsPopup({ show: false, applicantId: '' });
            ensureTutorialSubstage(tutorialInterviewSubstage, 'interview');
            moveDemoApplicant('_DEMO_APP_001', DEMO_INTERVIEW_SUBSTAGE.id);
            waitForElement(`[data-substage-id="${DEMO_INTERVIEW_SUBSTAGE.id}"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]`)
              .then(() => {
                scrollSubstageIntoView(DEMO_INTERVIEW_SUBSTAGE.id);
                scrollApplicantIntoView(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001');
                setTimeout(() => driverObj.moveNext(), 150);
              })
              .catch(error => {
                console.error('Interview substage not ready:', error);
                driverObj.moveNext();
              });
          },
          onPrevClick: () => {
            setApplicantDetailsPopup({ show: false, applicantId: '' });
            setTimeout(() => driverObj.movePrevious(), 200);
          }
        }
      },
      {
        element: () => {
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, true);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', true);
          scrollSubstageIntoView(DEMO_INTERVIEW_SUBSTAGE.id);
          scrollApplicantIntoView(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001');
          return document.querySelector(`[data-substage-id="${DEMO_INTERVIEW_SUBSTAGE.id}"]`);
        },
        popover: {
          title: uiText('🎯 Drag to the Interview Lane'),
          description: uiText("Advancing candidates is as simple as drag and drop.<br/><br/>We just moved Sarah into <strong>{{interview}}</strong> so you can see the flow.<br/><br/>When you drag a candidate to the interview lane, the scheduler will open automatically so you can lock in a time.", { interview: tutorialInterviewSubstage.title }),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => {
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, false);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', false);
          setTimeout(() => driverObj.moveNext(), 150);
        },
        onPrevClick: () => {
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, false);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'filter-applicants');
          reopenApplicantPopup();
          waitForElement('.applicant-details-popup, .popup-content')
            .then(() => driverObj.movePrevious())
            .catch(error => {
              console.error('Applicant popup not ready on prev:', error);
              driverObj.movePrevious();
            });
        }
      },
      {
        element: () => {
          ensureApplicantPosition('_DEMO_APP_001', DEMO_INTERVIEW_SUBSTAGE.id);
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, true);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', true);
          focusCandidateInSubstage(DEMO_INTERVIEW_SUBSTAGE.id);
          return document.querySelector(`[data-substage-id="${DEMO_INTERVIEW_SUBSTAGE.id}"]`);
        },
        popover: {
          title: uiText('📅 Schedule Right From Here'),
          description: uiText(`
            This lane keeps interviews organized, and the AI suggests times and sends invites automatically.<br/><br/>
          Let’s move Sarah along to her assessment.
        `),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => {
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, false);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', false);
          ensureTutorialSubstage(tutorialAssessmentSubstage, 'assessment');
          moveDemoApplicant('_DEMO_APP_001', DEMO_ASSESSMENT_SUBSTAGE.id);
          waitForElement(`[data-substage-id="${DEMO_ASSESSMENT_SUBSTAGE.id}"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]`)
            .then(() => {
              focusCandidateInSubstage(DEMO_ASSESSMENT_SUBSTAGE.id);
              setTimeout(() => driverObj.moveNext(), 150);
            })
            .catch(error => {
              console.error('Assessment stage not ready after interview step:', error);
              driverObj.moveNext();
            });
          return false;
        },
        onPrevClick: () => {
          highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, true);
          highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', true);
          focusCandidateInSubstage(DEMO_INTERVIEW_SUBSTAGE.id);
          setTimeout(() => {
            highlightSubstageArea(DEMO_INTERVIEW_SUBSTAGE.id, false);
            highlightSubstageCandidate(DEMO_INTERVIEW_SUBSTAGE.id, '_DEMO_APP_001', false);
            driverObj.movePrevious();
          }, 150);
        }
      },
      {
        element: () => {
          ensureApplicantPosition('_DEMO_APP_001', DEMO_ASSESSMENT_SUBSTAGE.id);
          focusCandidateInSubstage(DEMO_ASSESSMENT_SUBSTAGE.id);
          highlightSubstageArea(DEMO_ASSESSMENT_SUBSTAGE.id, true);
          highlightSubstageCandidate(DEMO_ASSESSMENT_SUBSTAGE.id, '_DEMO_APP_001', true);
          return document.querySelector(`[data-substage-id="${DEMO_ASSESSMENT_SUBSTAGE.id}"]`);
        },
        popover: {
          title: uiText('📝 Assessment Automation'),
          description: uiText(`
          Dropping a candidate here instantly:<br/><br/>
          • Sends the invite email<br/>
          • Tracks completion status<br/>
          • Scores responses out of 10<br/>
          • Summarizes every answer with AI insights<br/><br/>
          Let's advance Sarah to the offer stage.
        `),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => {
          highlightSubstageArea(DEMO_ASSESSMENT_SUBSTAGE.id, false);
          highlightSubstageCandidate(DEMO_ASSESSMENT_SUBSTAGE.id, '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'offer-sent');
          waitForElement('[data-substage-id="offer-sent"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]')
            .then(() => {
              scrollSubstageIntoView('offer-sent');
              scrollApplicantIntoView('offer-sent', '_DEMO_APP_001');
              setTimeout(() => driverObj.moveNext(), 150);
            })
            .catch(error => {
              console.error('Offer Sent stage not ready:', error);
              scrollSubstageIntoView('offer-sent');
              scrollApplicantIntoView('offer-sent', '_DEMO_APP_001');
              driverObj.moveNext();
            });
        },
        onPrevClick: () => {
          highlightSubstageArea(DEMO_ASSESSMENT_SUBSTAGE.id, false);
          highlightSubstageCandidate(DEMO_ASSESSMENT_SUBSTAGE.id, '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', DEMO_INTERVIEW_SUBSTAGE.id);
          setTimeout(() => {
            openScheduler();
            waitForElement('[data-tutorial="interview-scheduler-content"]')
              .then(() => driverObj.movePrevious())
              .catch(error => {
                console.error('Interview scheduler not ready on prev:', error);
                driverObj.movePrevious();
              });
          }, 200);
        }
      },
      {
        element: () => {
          ensureApplicantPosition('_DEMO_APP_001', 'offer-sent');
          focusCandidateInSubstage('offer-sent');
          highlightSubstageArea('offer-sent', true);
          highlightSubstageCandidate('offer-sent', '_DEMO_APP_001', true);
          return document.querySelector('[data-substage-id="offer-sent"]');
        },
        popover: {
          title: uiText('📧 Offer Sent Automations'),
          description: uiText(`
          Once a candidate hits Offer Sent:<br/><br/>
          • A branded offer email is delivered automatically<br/>
          • The candidate receives next steps & key contacts<br/>
          • Internal stakeholders get notified instantly<br/><br/>
          Move them to "Hired" to launch onboarding.
        `)
        },
        onNextClick: () => {
          highlightSubstageArea('offer-sent', false);
          highlightSubstageCandidate('offer-sent', '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'hired-applicants');
          setShowOnboardingFormTutorial(false);
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          waitForElement('[data-substage-id="hired-applicants"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]')
            .then(() => {
              scrollSubstageIntoView('hired-applicants');
              scrollApplicantIntoView('hired-applicants', '_DEMO_APP_001');
              setTimeout(() => driverObj.moveNext(), 150);
            })
            .catch(error => {
              console.error('Hired stage not ready:', error);
              scrollSubstageIntoView('hired-applicants');
              scrollApplicantIntoView('hired-applicants', '_DEMO_APP_001');
              driverObj.moveNext();
            });
        },
        onPrevClick: () => {
          highlightSubstageArea('offer-sent', false);
          highlightSubstageCandidate('offer-sent', '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', DEMO_ASSESSMENT_SUBSTAGE.id);
          setShowOnboardingFormTutorial(false);
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          setTimeout(() => driverObj.movePrevious(), 200);
        }
      },
      {
        element: () => {
          ensureApplicantPosition('_DEMO_APP_001', 'hired-applicants');
          focusCandidateInSubstage('hired-applicants');
          highlightSubstageArea('hired-applicants', true);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', true);
          return document.querySelector('[data-substage-id="hired-applicants"]');
        },
        popover: {
          title: uiText('🎉 Confirm the Hire'),
          description: uiText(`
          When you mark a candidate as hired:<br/><br/>
          • A congratulations email is sent automatically<br/><br/>
          Click "Next" to launch onboarding for Sarah.
        `),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => {
          highlightSubstageArea('hired-applicants', false);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', false);
          setTimeout(() => driverObj.moveNext(), 150);
        },
        onPrevClick: () => {
          highlightSubstageArea('hired-applicants', false);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'offer-sent');
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          setTimeout(() => driverObj.movePrevious(), 200);
        }
      },
      {
        element: () => {
          ensureApplicantPosition('_DEMO_APP_001', 'move-to-onboarding');
          focusCandidateInSubstage('move-to-onboarding');
          scrollSubstageIntoView('move-to-onboarding');
          const candidateElement = scrollApplicantIntoView('move-to-onboarding', '_DEMO_APP_001');
          highlightSubstageArea('move-to-onboarding', true);
          highlightSubstageArea('hired-applicants', true);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', true);
          if (candidateElement) {
            highlightSubstageCandidate('move-to-onboarding', '_DEMO_APP_001', true);
          }
          return document.querySelector('[data-substage-id="move-to-onboarding"]');
        },
        popover: {
          title: uiText('🚚 Move to Onboarding'),
          description: uiText(`
          Shift hires from the ATS into onboarding tasks:<br/><br/>
          • Drop the candidate into <strong>Move to Onboarding</strong><br/>
          • Transfer to the Onboarding system<br/>
          • Keep HR and IT in the loop automatically<br/><br/>
          Let's move Sarah forward to kick off onboarding.
        `),
          side: 'top',
          align: 'center'
        },
        onNextClick: () => {
          highlightSubstageArea('move-to-onboarding', false);
          highlightSubstageCandidate('move-to-onboarding', '_DEMO_APP_001', false);
          highlightSubstageArea('hired-applicants', false);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'move-to-onboarding');
          const demoApplicant = tutorialDemoApplicants.find(a => a.id === '_DEMO_APP_001');
          applicantsWorkspaceRef.current?.openOnboardingModalForTutorial('_DEMO_APP_001', demoApplicant);
          Promise.all([
            waitForElement('[data-substage-id="move-to-onboarding"] .applicant-cell[data-applicant-id="_DEMO_APP_001"]'),
            waitForElement('[data-tutorial="onboarding-modal"]')
          ])
            .then(() => {
              scrollSubstageIntoView('move-to-onboarding');
              scrollApplicantIntoView('move-to-onboarding', '_DEMO_APP_001');
              driverObj.moveNext();
            })
            .catch(error => {
              console.error('Onboarding modal not ready:', error);
              scrollSubstageIntoView('move-to-onboarding');
              scrollApplicantIntoView('move-to-onboarding', '_DEMO_APP_001');
              driverObj.moveNext();
            });
        },
        onPrevClick: () => {
          highlightSubstageArea('move-to-onboarding', false);
          highlightSubstageCandidate('move-to-onboarding', '_DEMO_APP_001', false);
          highlightSubstageArea('hired-applicants', false);
          highlightSubstageCandidate('hired-applicants', '_DEMO_APP_001', false);
          moveDemoApplicant('_DEMO_APP_001', 'hired-applicants');
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          setTimeout(() => driverObj.movePrevious(), 200);
        }
      },
      {
        element: () => {
          highlightOnboardingModal(true);
          return document.querySelector('[data-tutorial="onboarding-modal"]') || document.querySelector('.cv-upload-modal');
        },
        popover: {
          title: uiText('🎉 Hired → Onboarding'),
          description: uiText(`
          When you confirm a hire:<br/><br/>
          1. A congratulations email is sent automatically<br/>
          2. Capture start date, manager, and equipment needs<br/>
          3. Generate onboarding tasks for HR, IT, and managers<br/>
          4. Move the hire into the Onboarding workspace instantly<br/><br/>
          Click "Next" to send Sarah to Onboarding.
        `),
          side: 'left',
          align: 'center'
        },
        onNextClick: () => {
          highlightOnboardingModal(false);
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          moveDemoApplicant('_DEMO_APP_001', 'move-to-onboarding');
          waitForElementRemoval('[data-tutorial="onboarding-modal"]')
            .catch(() => waitForElementRemoval('.cv-upload-modal'))
            .then(() => {
              setTimeout(() => driverObj.moveNext(), 150);
            })
            .catch(error => {
              console.error('Onboarding modal did not close in time:', error);
              driverObj.moveNext();
            });
        },
        onPrevClick: () => {
          highlightOnboardingModal(false);
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          moveDemoApplicant('_DEMO_APP_001', 'hired-applicants');
          setTimeout(() => driverObj.movePrevious(), 150);
        }
      },
      {
        element: '[data-tutorial="ats-toolbar"]',
        popover: {
          title: uiText('🛠️ Quick Actions'),
          description: uiText(`
          Power features one click away:<br/><br/>
          • <strong>🔍 Global Search:</strong> Command+K to find anyone instantly<br/>
          • <strong>📤 Import CVs:</strong> Bulk upload resumes & auto-filter for jobs<br/>
          • <strong>📊 Analytics:</strong> Funnel, time-to-hire, and conversion dashboards<br/>
          • <strong>🔔 Notifications:</strong> New applicants, messages, assessments<br/><br/>
          Stay on top of every signal while you hire.
        `),
          side: 'bottom',
          align: 'center'
        },
        onPrevClick: () => {
          moveDemoApplicant('_DEMO_APP_001', 'hired-applicants');
          const demoApplicant = tutorialDemoApplicants.find(a => a.id === '_DEMO_APP_001');
          applicantsWorkspaceRef.current?.openOnboardingModalForTutorial('_DEMO_APP_001', demoApplicant);
          highlightOnboardingModal(true);
          setTimeout(() => driverObj.movePrevious(), 150);
        }
      },
      {
        popover: {
          title: uiText('🎉 ATS Complete!'),
          description: uiText(`
          You just completed the JOBSEEQR hiring loop:<br/><br/>
          ✅ Select a job and review AI-filtered fits<br/>
          ✅ Add custom interview & assessment stages<br/>
          ✅ Deep-dive into the full candidate profile<br/>
          ✅ Schedule interviews and send assessments instantly<br/>
          ✅ Send offers, move to hired, and launch onboarding automation<br/><br/>
          Ready to source fresh candidates from the Talent Search Engine?
        `)
        },
        onNextClick: () => {
          const continueToTSE = window.confirm(
            'Ready to see the Talent Search Engine (TSE)?\nSearch through 800M+ verified candidate profiles with AI-powered search.\nTakes 90 seconds.'
          );

          tutorialCompletedRef.current = true;
          tutorialCompletionActionRef.current = continueToTSE ? 'tse' : 'reload';
          driverObj.destroy();
          return false;
        },
        onPrevClick: () => {
          applicantsWorkspaceRef.current?.closeOnboardingModal?.();
          highlightOnboardingModal(false);
          moveDemoApplicant('_DEMO_APP_001', 'move-to-onboarding');
          setTimeout(() => driverObj.movePrevious(), 200);
        }
      }
    ];

    if (isRTL) {
      const arTitles = [
        '🚀 أهلاً بك في نظام تتبع المتقدمين JOBSEEQR',
        '📋 اختر الوظيفة',
        '🎯 مساحة عمل التوظيف',
        '🤖 جميع المتقدمين مقابل الأنسب',
        '➕ أضف خطوات المقابلات أو التقييم',
        '⚙️ إعداد الخطوات الجديدة',
        '🧱 خطوات نموذجية مضافة',
        '👤 مرشح مؤهل بالذكاء الاصطناعي',
        '📄 ملف المرشح المتعمق',
        '🎯 اسحب إلى مرحلة المقابلة',
        '📅 جدولة من هنا مباشرة',
        '📝 أتمتة التقييمات',
        '📧 أتمتة إرسال العروض',
        '🎉 تأكيد التعيين',
        '🚚 الانتقال إلى الإعداد الوظيفي',
        '🎉 تم التعيين → الإعداد الوظيفي',
        '🛠️ إجراءات سريعة',
        '🎉 تم إكمال دليل الـ ATS!'
      ];

      const arDescriptions = [
        `<strong>غرفة تحكم التوظيف لديك.</strong><br/><br/>• جمع كل المرشحين في مساحة واحدة<br/>• إبراز الأنسب بالذكاء الاصطناعي فورًا<br/>• إضافة مقابلات وتقييمات ومراجعات فريق<br/>• تعاون لحظي مع مديري التوظيف<br/><br/>سنقود مرشحًا حتى "تم التعيين".`,
        `اختر الوظيفة التي تريد مراجعتها.<br/><br/><em>حمّلنا "${job.title}" بثمانية مرشحين تجريبيين.</em>`,
        `سير عمل موحّد بثلاثة عروض:<br/><br/>• <strong>اللوحة:</strong> سحب المرشحين بين المراحل الفرعية<br/>• <strong>القائمة:</strong> فرز وتصفية بسرعة جدول البيانات<br/>• <strong>المعرض:</strong> بطاقات مرئية للمعاينة السريعة<br/><br/>هناك <strong>5 متقدمين</strong> و<strong>3 مؤهلين بالذكاء الاصطناعي</strong> لتجربة المسارين.`,
        `• <strong>جميع المتقدمين (5):</strong> كل من قدّم على الوظيفة<br/>• <strong>الأنسب (3):</strong> أفضل التطابقات بحسب الذكاء الاصطناعي<br/><br/>التحديث تلقائي كلما وصل مرشح جديد.`,
        `أضف مقابلة أو تقييم جديد من هنا لتخصيص مراحلك.`,
        `اضبط اسم المرحلة ونوعها (مقابلة، تقييم، مهمة)، وعيّن المسؤولين لجعل التدفق متوافقًا مع فريقك.`,
        `أضفنا خطوات نموذجية: مكالمة تمهيدية، مقابلة تقنية، تقييم، جولة مدير. يمكنك تعديلها أو حذفها.`,
        `هذا مرشح صنّفه الذكاء الاصطناعي كـ "مناسب جدًا" مع نقاط القوة الرئيسية.`,
        `افتح الملف الكامل لرؤية الخبرة والمهارات والملاحظات الداخلية ومراحل التقييم.`,
        `اسحب المرشح إلى مرحلة "المقابلة" لتحديث الحالة وإشعار الفريق.`,
        `جدولة المقابلة من بطاقة المرشح مباشرة مع اختيار التاريخ والوقت والمسؤول.`,
        `أرسل تقييمًا للمرشح (مثلاً اختبار مهارات أو دراسة حالة) وتابع النتيجة هنا.`,
        `أتمتة إرسال العرض الوظيفي من هنا مع قوالب بريد وملفات العرض.`,
        `تأكيد التعيين وتسجيل قرار اللجنة والملاحظات الأخيرة.`,
        `انقل المرشح إلى الإعداد الوظيفي لتوليد مهام الانضمام والوثائق.`,
        `تمت الترقية إلى الإعداد الوظيفي: المهام، المستندات، والوصول للأنظمة كلها جاهزة.`,
        `إجراءات سريعة: إرسال بريد، إضافة ملاحظة، تحديد مقابلة، أو تغيير المرحلة بنقرة.`,
        `<strong>أصبحت تعرف كيف:</strong><br/>✅ تدير اللوحات والقوائم والمعرض<br/>✅ تهيئ المقابلات والتقييمات<br/>✅ تستخدم الذكاء الاصطناعي لتقييم الملاءمة<br/>✅ تنقل المرشحين حتى التعيين والإعداد`
      ];

      steps.forEach((step, idx) => {
        if (step.popover) {
          step.popover.title = arTitles[idx] || step.popover.title;
          step.popover.description = arDescriptions[idx] || step.popover.description;
        }
      });
    }

    driverObj = driver({
      showProgress: true,
      steps,
      onDestroyStarted: () => {
        const wasCompleted = tutorialCompletedRef.current;
        cleanupATSDemoData();
        if (!wasCompleted) {
          window.location.reload();
        }
      },
      onDestroyed: async () => {
        const action = tutorialCompletionActionRef.current;
        tutorialCompletionActionRef.current = null;
        const tutorialCompleted = tutorialCompletedRef.current;
        tutorialCompletedRef.current = false;

        if (tutorialCompleted && userUID) {
          try {
            await set(ref(database, `companies/${userUID}/tutorialProgress/ats`), true);
          } catch (error) {
            console.error('Error saving tutorial progress:', error);
          }
        }

        if (action === 'tse') {
          navigate('/sourcing', { state: { startTutorial: true } });
        } else if (action === 'reload') {
          window.location.reload();
        }
      }
    });

    setDriverInstance(driverObj);
    driverObj.drive();
  };

  // Auto-start tutorial if navigated from Home
  useEffect(() => {
    if (location?.state?.startTutorial && userUID) {
      setTimeout(() => {
        startATSTutorial();
      }, 1500);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location, userUID]);

  // Listen for tutorial trigger from ProductProtectedRoute
  useEffect(() => {
    const handleTutorialFromOverlay = (event) => {
      console.log('[Applicants] Received startTutorialFromOverlay event', event.detail);
      const { onTutorialComplete } = event.detail;

      // Start the tutorial
      startATSTutorial();

      // Set up listener for when tutorial completes
      if (onTutorialComplete && driverInstance) {
        const originalOnDestroy = driverInstance.config?.onDestroyStarted;
        driverInstance.config = {
          ...driverInstance.config,
          onDestroyStarted: () => {
            if (originalOnDestroy) originalOnDestroy();
            onTutorialComplete();
          }
        };
      }
    };

    window.addEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    return () => {
      window.removeEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    };
  }, [driverInstance]);

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'var(--app-background, #031128)',
        color: 'var(--app-text, #FFFFFF)',
        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
          <p style={{ fontSize: '20px' }}>{t('loading')}</p>
        </div>
      </div>
    );
  }

  const isDemoApplicant = applicantDetailsPopup.applicantId?.startsWith('_DEMO_');
  const tutorialModeForPopup = tutorialActive || Boolean(isDemoApplicant);
  const tutorialApplicantData = tutorialModeForPopup
    ? (tutorialDemoApplicants.find(a => a.id === applicantDetailsPopup.applicantId) ||
      createAtsTutorialDataset(isRTL).applicants.find(a => a.id === applicantDetailsPopup.applicantId) ||
      null)
    : null;

  return (
    <div style={{ height: '100vh', backgroundColor: 'var(--app-background, #031128)', overflow: 'hidden' }}>
      <Sidebar />

      {/* Main Content - Wrapped with FeatureGate */}
      <div
        ref={mainContainerRef}
        style={{
          ...(isRTL
            ? { marginRight: 'var(--sidebar-width)', transition: 'margin-right 250ms ease' }
            : { marginLeft: 'var(--sidebar-width)', transition: 'margin-left 250ms ease' }),
          height: '100%',
          overflow: 'none',
          position: 'relative'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <FeatureGate showBanner={false}>
          {!teamAccess.loading && !canManageApplicants ? (
            <div
              style={{
                backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
                border: "1px solid var(--app-border, rgba(86, 141, 237, 0.2))",
                borderRadius: '12px',
                padding: '32px',
                color: 'var(--app-text, #FFFFFF)',
                maxWidth: '820px',
                margin: '40px auto 0'
              }}
            >
              <h2 style={{ margin: 0, fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '28px' }}>{uiText("Applicant management is restricted")}</h2>
              <p style={{ marginTop: '14px', marginBottom: 0, fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', lineHeight: 1.7, opacity: 0.82 }}>{uiText("Your team profile does not currently include applicant management access. An administrator needs to enable applicant access for your account from the team access controls.")}</p>
            </div>
          ) : (
            <>
          {/* Selection Box Overlay */}
          {isSelecting && (
            <div
              className="selection-box"
              style={{
                position: 'absolute',
                left: Math.min(selectionBox.startX, selectionBox.endX),
                top: Math.min(selectionBox.startY, selectionBox.endY),
                width: Math.abs(selectionBox.endX - selectionBox.startX),
                height: Math.abs(selectionBox.endY - selectionBox.startY),
                border: '2px solid #ffffff',
                backgroundColor: 'var(--app-surface, rgba(255, 255, 255, 0.12))',
                pointerEvents: 'none',
                zIndex: 10000
              }}
            />
          )}
          <ApplicantsWorkspace
            ref={applicantsWorkspaceRef}
            jobs={getJobsToShow()}
            selectedJobName={getSelectedJobToShow()}
            selectedJobData={selectedJobData}
            isFilterJobArea={isFilterJobArea}
            currActiveStage={currActiveStage}
            onSelectJob={selectJob}
            onToggleStage={toggleStage}
            onAddFilteringToJob={addFilteringToJob}
            onOpenFilteringSubstagePopup={openFilteringSubstagePopup}
            onOpenApplicantPopup={openApplicantPopup}
            onOpenAddSubstagePopup={openAddSubstagePopup}
            userUID={userUID}
            sortedApplicantsData={getApplicantsToShow()}
            currActiveJob={currActiveJob}
            exampleApplicants={tutorialDemoApplicants}
            dynamicSubstages={dynamicSubstages}
            onRemoveFilterSubstage={removeFilterSubstage}
            onOpenGlobalSearch={openGlobalSearch}
            onOpenNotifications={openNotifications}
            notificationCount={notificationCount}
            onCheckAndPromptForLink={checkAndPromptForLink}
            onOpenCVUpload={() => setShowCVUploadManager(true)}
            selectedApplicants={selectedApplicants}
            onClearSelection={() => setSelectedApplicants([])}
            pendingApprovals={pendingApprovals}
            canManageApplicants={canManageApplicants}
            jobId={currentJobId}
            onSubstageUpdated={handleSubstageUpdated}
            onSubstageDeleted={handleSubstageDeleted}
            onMultipleApplicantsRemoved={handleMultipleApplicantsRemoved}
          />
            </>
          )}
        </FeatureGate>
      </div>

      {/* Subscription Popup */}
      {subscriptionPopup && (
        <SubscriptionPopup
          isOpen={subscriptionPopup}
          onClose={closeSubscriptionPopup}
          userUID={userUID}
        />
      )}

      {tutorialActive && showOnboardingFormTutorial && (
        <>
          <div
            data-light-backdrop
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.35)',
              backdropFilter: 'blur(2px)',
              zIndex: 9998
            }}
          />
          <div
            data-tutorial="onboarding-form"
            data-light-surface="popover"
            style={{
              position: 'fixed',
              right: '60px',
              bottom: '60px',
              width: '380px',
              backgroundColor: '#ffffff',
              borderRadius: '18px',
              boxShadow: '0 22px 48px rgba(15, 23, 42, 0.25)',
              padding: '28px 32px',
              zIndex: 9999,
              fontFamily: 'Montserrat'
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#0f172a' }}>{uiText("Onboarding Details")}</h3>
            <p style={{ margin: '0 0 18px 0', color: '#475569', fontSize: '14px' }}>{uiText("Confirm the essentials for Sarah Johnson so tasks can be generated automatically.")}</p>

            <label style={{ display: 'block', fontSize: '13px', color: '#1e293b', marginBottom: '6px' }}>{uiText("Start Date")}</label>
            <input
              readOnly
              value="April 22, 2025"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #CBD5F5',
                marginBottom: '14px',
                fontSize: '14px'
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', color: '#1e293b', marginBottom: '6px' }}>{uiText("Manager")}</label>
            <input
              readOnly
              value="John Carter (Engineering Director)"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #CBD5F5',
                marginBottom: '14px',
                fontSize: '14px'
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', color: '#1e293b', marginBottom: '6px' }}>{uiText("Role & Location")}</label>
            <input
              readOnly
              value="Senior Software Engineer · Remote"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #CBD5F5',
                marginBottom: '18px',
                fontSize: '14px'
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', color: '#1e293b', marginBottom: '6px' }}>{uiText("Equipment")}</label>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #CBD5F5',
                marginBottom: '22px',
                fontSize: '14px',
                color: '#334155',
                backgroundColor: '#f8fafc'
              }}
            >{uiText("• 16\" MacBook Pro")}<br />{uiText("• SaaS access: GitHub, Linear, Notion")}<br />{uiText("• Home office stipend confirmation")}</div>

            <button
              type="button"
              style={{ '--light-control-fill': 'linear-gradient(135deg, #568ded, #3b82f6)',
                width: '100%',
                padding: '12px',
                borderRadius: '999px',
                border: 'none',
                background: 'linear-gradient(135deg, #568ded, #3b82f6)',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'default'
              }}
              data-light-control={"filled"}>{uiText("Next -&gt; Onboarding Workspace")}</button>
          </div>
        </>
      )}


      {/* Add Substage Popup */}
      {addSubstagePopup.show && (
        <AddSubstagePopup
          substageType={addSubstagePopup.type}
          onClose={closeAddSubstagePopup}
          currActiveStage={currActiveStage}
          onSubstageAdded={handleSubstageAdded}
          userUID={userUID}
        />
      )}

      {/* Filtering Substage Popup */}
      {filteringSubstagePopup && (
        <FilteringSubstagePopup
          onClose={closeFilteringSubstagePopup}
          currActiveJob={currActiveJob}
          onAddFilteringLayer={addFilteringLayer}
        />
      )}

      {/* Applicant Details Popup */}
      {applicantDetailsPopup.show && (
        <ApplicantDetailsPopup
          applicantId={applicantDetailsPopup.applicantId}
          connectionKey={applicantDetailsPopup.connectionKey}
          initialTab={applicantDetailsPopup.initialTab}
          onClose={closeApplicantPopup}
          userUID={userUID}
          onApplicantRemoved={handleApplicantRemoved}
          pendingApprovals={visiblePendingApprovals}
          onViewApprovalDetails={handleViewApprovalDetails}
          selectedJobId={currentJobId}
          selectedJobDescription={
            selectedJobData?.description ||
            selectedJobData?.jobDescription ||
            selectedJobData?.requirements ||
            selectedJobData?.details ||
            'Job description not available'
          }
          selectedJobDepartment={selectedJobData?.data?.department || selectedJobData?.department || ''}
          workflowStages={workflowStages}
          dynamicSubstages={dynamicSubstages}
          aiAssessmentSubstages={aiAssessmentSubstages}
          tutorialMode={tutorialModeForPopup}
          tutorialData={tutorialApplicantData}
        />
      )}

      {/* Global Applicant Search Popup */}
      {isGlobalSearchOpen && (
        <GlobalApplicantSearch
          isOpen={isGlobalSearchOpen}
          onClose={closeGlobalSearch}
          applicants={allApplicants}
          onSelectApplicant={handleGlobalApplicantSelect}
        />
      )}

      {/* Notifications Popup */}
      {isNotificationsOpen && (
        <NotificationsPopup
          isOpen={isNotificationsOpen}
          onClose={closeNotifications}
          notifications={notifications}
          onSelectNotification={handleNotificationSelect}
          pendingApprovals={visiblePendingApprovals}
          companyUID={userUID}
          onViewApprovalDetails={handleViewApprovalDetails}
          onApprovalComplete={(status, approval) => {
            // Refresh job posts if a job was approved/rejected
            if (approval.type === 'job_posting') {
              // The listener will auto-update pendingApprovals
              console.log(`Approval ${approval.id} ${status}`);
            }
          }}
        />
      )}

      {/* Interview Scheduler */}
      {(CALENDAR_ENABLED || tutorialActive) && showInterviewScheduler && schedulerApplicantId && (
        <InterviewScheduler
          applicantId={schedulerApplicantId}
          userUID={userUID}
          onClose={closeInterviewScheduler}
          onScheduleSuccess={handleInterviewScheduled}
          jobTitle={selectedJobData?.jobTitle || selectedJobName}
          companyName={selectedJobData?.companyName || ''}
          substageTitle={schedulerSubstageData?.title || schedulerSubstageData?.name || ''}
          substageData={schedulerSubstageData}
        />
      )}

      {/* Calendar Connection Prompt */}
      {(CALENDAR_ENABLED || tutorialActive) && showCalendarConnectionPrompt && schedulerApplicantId && (
        <CalendarConnectionPrompt
          onClose={closeCalendarConnectionPrompt}
          onConnectCalendar={handleConnectCalendar}
          onScheduleManually={handleScheduleManually}
          candidateName={getCandidateName(schedulerApplicantId)}
        />
      )}

      {/* CV Upload Manager */}
      {showCVUploadManager && (
        <CVUploadManager
          isOpen={showCVUploadManager}
          onClose={() => setShowCVUploadManager(false)}
          jobs={jobs}
          onUploadComplete={handleCVUploadComplete}
          onDataRefresh={handleDataRefresh}
          employerUID={userUID}
        />
      )}
    </div>
  );

  // Handle CV upload complete
  function handleCVUploadComplete() {
    setShowCVUploadManager(false);
    // Refresh the applicants data to show newly uploaded CVs
    loadAllConnectionsAndUpdateDropdown(userUID);
  }

  // Handle data refresh without closing modal (for custom list creation)
  function handleDataRefresh() {
    console.log('🔄 Refreshing applicants data without closing modal');
    // Refresh the applicants data to show newly created custom lists and uploaded CVs
    loadAllConnectionsAndUpdateDropdown(userUID);
  }

};

export default Applicants; 
